/* DocumentsPage.jsx — ChainFlow "Documents Hub".
 *
 * Premium operational document layer for property transactions.
 *
 * Doctrine:
 *   - The sidebar / shell / nav are NEVER modified by this page. We
 *     render strictly inside `md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out` to the right of the
 *     existing Sidebar.
 *   - Calm, premium, operational. Linear × Stripe × Apple × Notion.
 *   - Hybrid view: workspace-wide summary cards + per-deal operational
 *     detail. A subtle deal-context pill at the top scopes the rest of
 *     the page to one transaction.
 *   - No new backend endpoints. Required Docs are a canonical
 *     deterministic list overlaid on the deal's uploaded documents
 *     (via the existing `/api/deals/{id}/documents` endpoint).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ClipboardList, Upload, UploadCloud, Eye, Download, FileText, IdCard, Banknote,
  Landmark, FileSignature, FileSearch, ShieldCheck, ShieldAlert, Home,
  Sofa, FolderOpen, FileBadge, Wrench, Receipt, AlertCircle, Loader2,
  ChevronDown, Plus, Check, Clock, Lock, Filter as FilterIcon,
  CalendarClock, Sparkles, MoreHorizontal, ArrowRight, ArrowUpRight,
  RefreshCw, Trash2, X, User,
} from "lucide-react";
import { Sidebar } from "../components/Sidebar";
import { useAuth } from "../data/authStore";
import { PageHeader, WORKSPACE_SHELL_CLASS } from "../components/ui/PageHeader";
import { useChainStore } from "../data/chainStore";
import { api } from "../lib/api";
import { clientPreflight, uploadErrorPresentation } from "../lib/uploadErrors";
import { useDealActivity, invalidateDealActivity } from "../hooks/useDealActivity";
import { computeRung } from "../lib/chaseLadder";
import { ChaseLadderRung } from "../components/operational/ChaseLadderRung";
import { DocumentsHubOverview } from "../components/documents/DocumentsHubOverview";
import { bsaStatus, optsForDeal } from "../data/transactionJourney";
import { MissingAcrossDealsTab } from "../components/documents/MissingAcrossDealsTab";

/* ═══════════════════════════════════════════════════════════════════
   Canonical UK property-transaction document taxonomy.

   Each entry has:
     id           — stable key (used for status lookup)
     label        — operator-facing name
     responsible  — who is expected to provide it
     icon         — lucide icon
     group        — "core" | "additional"
     stageGate    — earliest deal stage from which this doc starts
                    blocking exchange (used by operational blockers)
     leasehold    — true if the doc only applies to leasehold deals
     cladding     — true if the doc only applies to flats / blocks
     keywords     — name-match terms to map uploaded files onto the
                    canonical row (case-insensitive)
   ─────────────────────────────────────────────────────────────────── */
const REQUIRED_DOCS = [
  // ─── Core ──────────────────────────────────────────────────────
  { id: "id_verif",   label: "ID Verification",          responsible: "Buyer",         icon: IdCard,        group: "core", stageGate: "aml",          keywords: ["id ver", "id check", "passport", "driving licen", "kyc"] },
  { id: "proof_funds", label: "Proof of Funds",           responsible: "Buyer",         icon: Banknote,      group: "core", stageGate: "aml",          keywords: ["proof of funds", "funds proof", "bank statement"] },
  { id: "mortgage",    label: "Mortgage Agreement",       responsible: "Buyer",         icon: Landmark,      group: "core", stageGate: "mortgage_app", keywords: ["mortgage agreement", "mortgage offer", "mortgage in principle"] },
  { id: "memo",        label: "Memorandum of Sale",       responsible: "Agent",         icon: FileText,      group: "core", stageGate: "memo",         keywords: ["memorandum", "memo of sale", "mos"] },
  { id: "draft",       label: "Draft Contract",           responsible: "Seller Solicitor", icon: FileSignature, group: "core", stageGate: "solicitors", keywords: ["draft contract", "contract pack"] },
  { id: "searches",    label: "Searches",                 responsible: "Buyer Solicitor", icon: FileSearch,  group: "core", stageGate: "searches",     keywords: ["search", "local authority search", "water and drainage", "environmental search"] },
  { id: "exchange_deposit", label: "Exchange Deposit Proof", responsible: "Buyer",      icon: ShieldCheck,   group: "core", stageGate: "exchange_ready", keywords: ["deposit proof", "exchange deposit"] },

  // ─── Seller / Conveyancing ────────────────────────────────────
  { id: "ta6",         label: "TA6 Property Information Form", responsible: "Seller", icon: Home,          group: "core", stageGate: "solicitors", keywords: ["ta6", "property information form"] },
  { id: "ta10",        label: "TA10 Fittings & Contents Form", responsible: "Seller", icon: Sofa,          group: "core", stageGate: "solicitors", keywords: ["ta10", "fittings", "contents"] },
  { id: "piq",         label: "Property Information Questionnaire", responsible: "Seller", icon: FileText,  group: "additional", stageGate: "enquiries", keywords: ["property information questionnaire", "piq"] },
  { id: "indemnity",   label: "Indemnity Policies",       responsible: "Seller Solicitor", icon: ShieldAlert, group: "additional", stageGate: "enquiries", keywords: ["indemnity"] },

  // ─── Leasehold ────────────────────────────────────────────────
  { id: "mgmt_pack",   label: "Management Pack",          responsible: "Seller",        icon: FolderOpen,    group: "core", stageGate: "solicitors", leasehold: true,  keywords: ["management pack"] },
  { id: "service_chg", label: "Service Charge Statements", responsible: "Seller",       icon: Receipt,       group: "additional", stageGate: "enquiries", leasehold: true, keywords: ["service charge"] },
  { id: "ground_rent", label: "Ground Rent Statements",   responsible: "Seller",        icon: Receipt,       group: "additional", stageGate: "enquiries", leasehold: true, keywords: ["ground rent"] },
  { id: "lpe1",        label: "LPE1 Pack",                responsible: "Managing Agent", icon: FolderOpen,    group: "additional", stageGate: "enquiries", leasehold: true, keywords: ["lpe1"] },
  { id: "headlease",   label: "Headlease",                responsible: "Seller",        icon: FileText,      group: "additional", stageGate: "enquiries", leasehold: true, keywords: ["headlease", "head lease"] },
  { id: "leasehold_pack", label: "Leasehold Information Pack", responsible: "Managing Agent", icon: FolderOpen, group: "additional", stageGate: "enquiries", leasehold: true, keywords: ["leasehold information"] },
  { id: "licence_assign", label: "Licence to Assign",     responsible: "Managing Agent", icon: FileBadge,    group: "additional", stageGate: "exchange_ready", leasehold: true, keywords: ["licence to assign", "license to assign"] },
  { id: "deed_covenant", label: "Deed of Covenant",       responsible: "Seller Solicitor", icon: FileBadge,  group: "additional", stageGate: "exchange_ready", leasehold: true, keywords: ["deed of covenant"] },
  { id: "share_cert",  label: "Share / Membership Certificate", responsible: "Seller",  icon: FileBadge,     group: "additional", stageGate: "exchange_ready", leasehold: true, keywords: ["share certificate", "membership certificate"] },
  // iter-alignment · P71 · Complete Document Catalogue (2026-02).
  // Additional leasehold instruments that operators routinely
  // exchange during the pre-exchange enquiries phase.  Every
  // row is `leasehold: true` so `deriveRows` drops them from
  // freehold deals automatically — reusing the SAME gate as
  // the rest of the Leasehold cluster; no parallel taxonomy.
  { id: "lease",           label: "Lease",                    responsible: "Seller Solicitor", icon: FileSignature, group: "core",       stageGate: "solicitors", leasehold: true, keywords: ["lease", "leasehold deed", "registered lease"] },
  { id: "deed_variation",  label: "Deed of Variation",        responsible: "Seller Solicitor", icon: FileBadge,     group: "additional", stageGate: "enquiries",  leasehold: true, keywords: ["deed of variation", "variation of lease"] },
  { id: "alteration_licence", label: "Licence / Consent for Alterations", responsible: "Managing Agent", icon: FileBadge, group: "additional", stageGate: "enquiries", leasehold: true, keywords: ["licence for alterations", "license for alterations", "consent for alterations", "alteration consent"] },

  // ─── Cladding / Compliance ────────────────────────────────────
  { id: "ews1",        label: "EWS1 Certificate",         responsible: "Seller",        icon: ShieldAlert,   group: "additional", stageGate: "enquiries", cladding: true, keywords: ["ews1"] },
  { id: "ews1_cover",  label: "EWS1 Cover Letter",        responsible: "Seller",        icon: FileText,      group: "additional", stageGate: "enquiries", cladding: true, keywords: ["ews1 cover"] },
  { id: "fire_risk",   label: "Fire Risk Assessment",     responsible: "Managing Agent", icon: ShieldAlert,   group: "additional", stageGate: "enquiries", cladding: true, keywords: ["fire risk"] },
  { id: "buildings_ins", label: "Buildings Insurance Schedule", responsible: "Managing Agent", icon: ShieldCheck, group: "additional", stageGate: "enquiries", cladding: true, keywords: ["buildings insurance"] },

  // ─── Certificates ─────────────────────────────────────────────
  { id: "fensa",       label: "FENSA / Window Certificates", responsible: "Seller",       icon: Wrench,        group: "additional", stageGate: "enquiries", keywords: ["fensa", "window certificate", "window cert"] },
  { id: "gas_safety",  label: "Gas Safety Certificate",   responsible: "Seller",        icon: Wrench,        group: "additional", stageGate: "enquiries", keywords: ["gas safety"] },
  { id: "eicr",        label: "EICR (Electrical Certificate)", responsible: "Seller",    icon: Wrench,        group: "additional", stageGate: "enquiries", keywords: ["eicr", "electrical certificate", "electrical install"] },
  { id: "nhbc",        label: "NHBC / LABC Warranty",     responsible: "Seller",        icon: ShieldCheck,   group: "additional", stageGate: "enquiries", keywords: ["nhbc", "labc", "new build warranty", "premier guarantee"] },
  { id: "planning",    label: "Planning Permission",      responsible: "Seller",        icon: FileText,      group: "additional", stageGate: "enquiries", keywords: ["planning permission"] },
  { id: "building_reg", label: "Building Regulation Certificates", responsible: "Seller", icon: FileText,    group: "additional", stageGate: "enquiries", keywords: ["building reg"] },
  // iter-alignment · P71 · Boiler service records live under
  // Compliance alongside gas / electrical certs.
  { id: "boiler_service", label: "Boiler Service Record", responsible: "Seller",        icon: Wrench,        group: "additional", stageGate: "enquiries", keywords: ["boiler service", "boiler cert", "boiler record"] },

  // ─── Additional ───────────────────────────────────────────────
  { id: "probate",     label: "Probate Documents",        responsible: "Seller Solicitor", icon: FileText,   group: "additional", stageGate: "solicitors", keywords: ["probate"] },
  { id: "damp",        label: "Damp Reports",             responsible: "Seller",        icon: FileText,      group: "additional", stageGate: "enquiries", keywords: ["damp report", "damp survey"] },

  // ─── Repairs & Maintenance (iter-alignment · P70) ─────────────
  { id: "contractor_quote",    label: "Contractor Quote",           responsible: "Seller",  icon: Wrench,   group: "additional", stageGate: "enquiries", keywords: ["contractor quote", "builder quote", "trade quote"] },
  { id: "contractor_invoice",  label: "Contractor Invoice",         responsible: "Seller",  icon: Receipt,  group: "additional", stageGate: "enquiries", keywords: ["contractor invoice", "builder invoice", "trade invoice"] },
  { id: "repair_report",       label: "Repair Report",              responsible: "Seller",  icon: FileText, group: "additional", stageGate: "enquiries", keywords: ["repair report", "maintenance report"] },
  { id: "repair_evidence",     label: "Repair Evidence Photos",     responsible: "Seller",  icon: FileText, group: "additional", stageGate: "enquiries", keywords: ["repair evidence", "repair photos", "before after photos"] },
  { id: "works_docs",          label: "Works Documentation",        responsible: "Seller",  icon: FileText, group: "additional", stageGate: "enquiries", keywords: ["works documentation", "works completed", "building works"] },

  // ─── Utilities (iter-alignment · P70) ─────────────────────────
  { id: "utility_gas",         label: "Gas Utility Statement",      responsible: "Seller",  icon: Receipt,  group: "additional", stageGate: "enquiries", keywords: ["gas bill", "gas statement", "gas supplier"] },
  { id: "utility_electricity", label: "Electricity Utility Statement", responsible: "Seller", icon: Receipt, group: "additional", stageGate: "enquiries", keywords: ["electricity bill", "electricity statement", "electric supplier"] },
  { id: "utility_water",       label: "Water Utility Statement",    responsible: "Seller",  icon: Receipt,  group: "additional", stageGate: "enquiries", keywords: ["water bill", "water statement", "water supplier"] },
  { id: "utility_broadband",   label: "Broadband / Connectivity",   responsible: "Seller",  icon: Receipt,  group: "additional", stageGate: "enquiries", keywords: ["broadband", "internet supplier", "fibre"] },
  { id: "meter_readings",      label: "Meter Readings",             responsible: "Seller",  icon: FileText, group: "additional", stageGate: "completed",  keywords: ["meter reading", "final meter", "closing meter"] },

  // ─── New Build (iter-alignment · P71) ─────────────────────────
  // Snagging + developer packs live under Property (the
  // property-facts lane).  NHBC / LABC warranty already lives
  // under Compliance and covers "or equivalent" via LABC /
  // Premier Guarantee keywords.
  { id: "snagging",        label: "Snagging Report",           responsible: "Buyer",  icon: FileText, group: "additional", stageGate: "enquiries", keywords: ["snagging", "snag list", "defects list"] },
  { id: "developer_docs",  label: "Developer Documentation",   responsible: "Seller", icon: FolderOpen, group: "additional", stageGate: "solicitors", keywords: ["developer documentation", "developer pack", "developer handbook", "new build handover pack"] },

  // ─── Parking & Storage (iter-alignment · P71) ────────────────
  // Parking lease/title lives under Legal (a distinct title /
  // lease document); allocation + storage evidence lives under
  // Management alongside the wider service-charge / management
  // cluster.
  { id: "parking_title",       label: "Parking Lease / Title",             responsible: "Seller Solicitor", icon: FileSignature, group: "additional", stageGate: "solicitors", keywords: ["parking title", "parking lease", "parking deed"] },
  { id: "parking_allocation",  label: "Parking Space Allocation",          responsible: "Managing Agent",   icon: FolderOpen,    group: "additional", stageGate: "enquiries",  keywords: ["parking allocation", "allocated parking", "parking space allocation"] },
  { id: "storage_docs",        label: "Storage Lease / Allocation",        responsible: "Managing Agent",   icon: FolderOpen,    group: "additional", stageGate: "enquiries",  keywords: ["storage lease", "storage allocation", "storage cage"] },

  // ─── Warranties & Guarantees (iter-alignment · P71) ──────────
  // Transferable warranties / appliance guarantees sit under
  // Repairs & Maintenance so they surface alongside the works
  // evidence that produced them.  Works guarantee is separate
  // from the general Works Documentation row so operators can
  // request the guarantee specifically.
  { id: "appliance_warranty",    label: "Appliance Warranty",             responsible: "Seller", icon: ShieldCheck, group: "additional", stageGate: "enquiries", keywords: ["appliance warranty", "appliance guarantee"] },
  { id: "works_guarantee",       label: "Works Guarantee",                responsible: "Seller", icon: ShieldCheck, group: "additional", stageGate: "enquiries", keywords: ["works guarantee", "works warranty", "installation guarantee"] },
  { id: "warranty_transferable", label: "Transferable Warranty / Guarantee", responsible: "Seller", icon: ShieldCheck, group: "additional", stageGate: "enquiries", keywords: ["transferable warranty", "transferable guarantee", "roof warranty", "damp warranty", "damp guarantee"] },

  // ─── Tenancy (iter-alignment · P71) ──────────────────────────
  // Tenancy-adjacent documents map into Management alongside
  // the wider let-property administration cluster.
  { id: "rent_arrears",        label: "Rent Arrears Statement",           responsible: "Managing Agent", icon: Receipt,  group: "additional", stageGate: "enquiries", keywords: ["rent arrears", "arrears statement", "outstanding rent"] },
  { id: "tenancy_deposit",     label: "Tenancy Deposit Evidence",         responsible: "Managing Agent", icon: ShieldCheck, group: "additional", stageGate: "enquiries", keywords: ["tenancy deposit", "deposit repayment", "deposit transfer", "deposit protection"] },

  // ─── Completion / Handover (iter-alignment · P71) ────────────
  // Completion-day artefacts live under the existing Completion
  // lane — previously empty; now populated with the canonical
  // handover set.
  { id: "completion_statement", label: "Completion Statement",   responsible: "Seller Solicitor", icon: Receipt,  group: "core",       stageGate: "completed", keywords: ["completion statement", "completion account"] },
  { id: "keys_inventory",       label: "Keys & Fobs Inventory",  responsible: "Agent",            icon: FileText, group: "additional", stageGate: "completed", keywords: ["keys inventory", "keys and fobs", "fobs inventory", "key handover"] },
  { id: "handover_docs",        label: "Handover Documents",     responsible: "Seller",           icon: FolderOpen, group: "additional", stageGate: "completed", keywords: ["handover documents", "handover pack", "moving handover"] },
];

// Stage rank — used by operational-blocker derivation to flag docs
// that are already past their stage gate (i.e. truly blocking).
const STAGE_RANK = {
  offer: 0, aml: 1, memo: 2, solicitors: 3, mortgage_app: 4, survey: 5,
  searches: 6, enquiries: 7, mortgage_offer: 8, exchange_ready: 9,
  exchanged: 10, completed: 11,
};

// Status tone tokens — calm muted operational palette.
const STATUS_TONE = {
  Missing:        { bg: "#FEF2F2", fg: "#B91C1C", ring: "rgba(239,68,68,0.22)",  dot: "#EF4444" },
  Requested:      { bg: "#FFFBEB", fg: "#B45309", ring: "rgba(245,158,11,0.22)", dot: "#F59E0B" },
  // iter-alignment · P72 · Stage-gated pre-requirement row.
  // Calm slate so the row reads as "on schedule, nothing to
  // do yet" — never as a blocker.
  "Not yet required": { bg: "#F8FAFC", fg: "#64748B", ring: "rgba(148,163,184,0.22)", dot: "#94A3B8" },
  "Pending BSA confirmation": { bg: "#F8FAFC", fg: "#475569", ring: "rgba(100,116,139,0.28)", dot: "#64748B" },
  // iter-368 — Operational vocabulary. "Needs Review" absorbs the
  // legacy "Uploaded" bucket (something has arrived and is
  // waiting for a human to check it). "Reviewed" replaces the
  // former "Under Review" (someone has actioned the review but
  // it isn't yet signed off / approved).
  "Needs Review": { bg: "#F5F3FF", fg: "#5B21B6", ring: "rgba(139,92,246,0.22)", dot: "#8B5CF6" },
  Reviewed:       { bg: "#EFF6FF", fg: "#1D4ED8", ring: "rgba(59,130,246,0.22)", dot: "#3B82F6" },
  Approved:       { bg: "#ECFDF5", fg: "#047857", ring: "rgba(16,185,129,0.22)", dot: "#10B981" },
  Blocked:        { bg: "#FEF2F2", fg: "#991B1B", ring: "rgba(220,38,38,0.28)",  dot: "#DC2626" },
  Expired:        { bg: "#F8FAFC", fg: "#475569", ring: "rgba(100,116,139,0.22)", dot: "#64748B" },
};

const fmtDateUK = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const initialsOf = (s) =>
  String(s || "?").trim().split(/\s+/).slice(0, 2).map((p) => p[0] || "").join("").toUpperCase();

const relTime = (v) => {
  if (!v) return "";
  const t = typeof v === "number" ? v : Date.parse(v);
  if (Number.isNaN(t)) return "";
  const ms = Date.now() - t;
  if (ms < 60_000) return "just now";
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
};

// ═══════════════════════════════════════════════════════════════════
//  Property Transaction Library — display taxonomy
// ═══════════════════════════════════════════════════════════════════
//
// The Library sub-nav is a FILTER over the existing SSOT — never
// a new folder/storage layer.  Each REQUIRED_DOCS row is mapped
// to exactly one display category so the operator can traverse
// the workspace by workflow lane (Property · Sales · Legal …)
// instead of the flat "Required + Additional" split.
//
// Missing Documents + Requests are counts that already live on
// each row's `status` field.  All other categories are pure
// groupings over the canonical taxonomy.
// iter-alignment · P70 · Document Centre · Taxonomy Expansion
// (2026-02).  The prior "Management/BSA" bucket is split into
// two operationally-distinct categories so the Library lane
// map matches the way an operator actually thinks about the
// documents.  The BSA lane is gated at the ROW level via the
// existing `cladding: true` flag AND at the NAV level via the
// canonical `deal.applicableBSA` SSOT (see `bsaApplies` prop
// on `PropertyTransactionLibrary`).  Repairs & Maintenance +
// Utilities are new lanes with dedicated REQUIRED_DOCS rows
// so operators can request / track them.
const CATEGORY_OF_DOC = {
  // Property / seller identity + property facts
  ta6: "Property", ta10: "Property", piq: "Property",
  // iter-alignment · P71 · New Build docs sit under Property.
  snagging: "Property", developer_docs: "Property",
  // Sales — the operator-generated sale bundle
  memo: "Sales",
  // Legal
  draft: "Legal", searches: "Legal", indemnity: "Legal", probate: "Legal",
  // iter-alignment · P71 · Parking title/lease is a Legal instrument.
  parking_title: "Legal",
  // Management (management pack, service charge, ground rent, insurance)
  mgmt_pack: "Management", service_chg: "Management",
  ground_rent: "Management", lpe1: "Management",
  headlease: "Management", leasehold_pack: "Management",
  licence_assign: "Management", deed_covenant: "Management",
  share_cert: "Management",
  buildings_ins: "Management",
  // iter-alignment · P71 · Leasehold instruments (lease itself +
  // deed of variation + alteration licence) join the Management
  // cluster alongside the wider leasehold documentation.
  lease: "Management", deed_variation: "Management",
  alteration_licence: "Management",
  // iter-alignment · P71 · Parking allocation + storage docs
  // are managing-agent artefacts.
  parking_allocation: "Management", storage_docs: "Management",
  // iter-alignment · P71 · Tenancy-adjacent statements sit under
  // Management (property administration).
  rent_arrears: "Management", tenancy_deposit: "Management",
  // BSA (landlord / leaseholder certificates, remediation, fire safety)
  ews1: "BSA", ews1_cover: "BSA",
  fire_risk: "BSA",
  // Mortgage & financial
  mortgage: "Mortgage & Financial", proof_funds: "Mortgage & Financial",
  // Compliance (regulatory + property compliance certificates)
  id_verif: "Compliance",
  fensa: "Compliance", gas_safety: "Compliance", eicr: "Compliance",
  nhbc: "Compliance", planning: "Compliance", building_reg: "Compliance",
  // iter-alignment · P71 · Boiler service record joins the
  // compliance certificate cluster.
  boiler_service: "Compliance",
  // Repairs & Maintenance (contractor + works evidence)
  contractor_quote: "Repairs & Maintenance",
  contractor_invoice: "Repairs & Maintenance",
  repair_report: "Repairs & Maintenance",
  repair_evidence: "Repairs & Maintenance",
  works_docs: "Repairs & Maintenance",
  damp: "Repairs & Maintenance",
  // iter-alignment · P71 · Appliance / works / transferable
  // warranties travel with the works evidence they document.
  appliance_warranty: "Repairs & Maintenance",
  works_guarantee: "Repairs & Maintenance",
  warranty_transferable: "Repairs & Maintenance",
  // Utilities (gas · electricity · water · broadband · meters)
  utility_gas: "Utilities",
  utility_electricity: "Utilities",
  utility_water: "Utilities",
  utility_broadband: "Utilities",
  meter_readings: "Utilities",
  // Exchange
  exchange_deposit: "Exchange",
  // iter-alignment · P71 · Completion / handover artefacts.
  completion_statement: "Completion",
  keys_inventory: "Completion",
  handover_docs: "Completion",
};

// iter-alignment · P70 · Category display order.  BSA is
// filtered out of the nav at render-time when the deal's
// canonical `applicableBSA` SSOT is falsy — see
// `PropertyTransactionLibrary`.
const LIBRARY_CATEGORIES = [
  "Property",
  "Sales",
  "Legal",
  "Management",
  "BSA",
  "Mortgage & Financial",
  "Compliance",
  "Repairs & Maintenance",
  "Utilities",
  "Exchange",
  "Completion",
];

const categoryOf = (rowId) => CATEGORY_OF_DOC[rowId] || "Property";


function deriveRows({ tenure, deal, uploaded, requests }) {
  const t = String(tenure || deal?.tenure || "").toLowerCase();
  const isLeasehold = t === "leasehold";
  const bsa = bsaStatus(t, optsForDeal(deal || {}));

  const uploadedNorm = (uploaded || []).map((d) => ({
    ...d,
    _lc: String(d.name || "").toLowerCase(),
    _tag: String(d.tag || d.doc_type || "").toLowerCase(),
  }));
  const requestNorm = (requests || []).map((r) => ({
    ...r,
    _lc: String(r.doc_type || r.title || "").toLowerCase(),
  }));

  const rows = REQUIRED_DOCS.filter((d) => {
    // iter-alignment · P0 · Document Centre Counts Audit (2026-02-28)
    // -----------------------------------------------------------------
    // Row eligibility MUST match the doc's documented applicability
    // flags.  Previously only `leasehold` was honoured; `cladding:
    // true` was documented but silently ignored so freehold-house
    // deals kept surfacing EWS1 / EWS1 Cover / Fire Risk / Buildings
    // Insurance as expected + missing.  In the UK the cladding /
    // block-safety pack applies exclusively to flats / blocks —
    // which in ChainFlow's canonical property model is equivalent
    // to `tenure === "leasehold"` (freehold flats are a rounding
    // error).  Aligning `cladding` with the same `isLeasehold`
    // gate drops those 4 docs from freehold-house deals so they
    // no longer inflate All / Missing / Management-BSA counts.
    // No new endpoint, no duplicate state — the SAME derived
    // `rows` array continues to drive every count on the page.
    /* iter-alignment · P70 · BSA gate uses canonical SSOT.
     * Previously the `cladding: true` rows were dropped from
     * freehold deals only.  BSA applicability is now driven by
     * the SAME `deal.applicableBSA` flag the journey / rail
     * respects — `bsa_applies` on the property is the single
     * source of truth (see routes_deals.py fan-out).  Default
     * (no explicit flag): leasehold ⇒ BSA applies. */
    if (d.leasehold && !isLeasehold) return false;
    if (d.cladding && bsa === "no") return false;
    return true;
  }).map((d) => {
    // Match an uploaded file by tag → keyword
    const upMatch =
      uploadedNorm.find((u) => u._tag && u._tag === d.id) ||
      uploadedNorm.find((u) => d.keywords.some((k) => u._lc.includes(k)));

    const reqMatch =
      !upMatch &&
      requestNorm.find((r) => d.keywords.some((k) => r._lc.includes(k)));

    let status = "Missing";
    if (upMatch) {
      const s = String(upMatch.status || "").toLowerCase();
      if (s === "approved") status = "Approved";
      else if (s === "blocked") status = "Blocked";
      else if (s === "expired") status = "Expired";
      // iter-368 — legacy `under_review` / `review` maps to
      // "Reviewed" (someone has actioned it); every other
      // uploaded state maps to "Needs Review" (arrived but
      // nobody's looked yet).
      else if (s === "under_review" || s === "review" || s === "reviewed") status = "Reviewed";
      else status = "Needs Review";
    } else if (reqMatch) {
      const rs = String(reqMatch.status || "").toLowerCase();
      if (rs === "fulfilled") status = "Approved";
      else status = "Requested";
    }

    /* iter-alignment · P72 · Stage-gate downgrade (2026-02).
     *
     * A row with no file and no live request is now "Not yet
     * required" until the deal reaches (or passes) the row's
     * `stageGate` — neutral, excluded from missing totals, no
     * chase action.  This is the SHARED calculation every
     * consumer reads via `r.status`:
     *   • Library nav counts (`counts.missing` = rows with
     *     `status === "Missing"`)
     *   • Category / status filters (filter by `r.status`)
     *   • DocumentsHubOverview urgent-triage panel
     *     (`if (r.status !== "Missing") continue`)
     *   • Chase actions (rendered only when status is Missing
     *     / Requested)
     *   • Blocker engine (backend reads canonical `deals.stage`
     *     + `deals.documents` — this frontend downgrade doesn't
     *     regress backend rules)
     * Uploaded / Requested / Needs Review / Reviewed / Approved
     * / Blocked / Expired statuses are unchanged — the gate
     * only affects the empty-row default.  When either stage
     * is unknown we retain "Missing" so we never silently bury
     * a genuinely-outstanding document. */
    if (status === "Missing" && d.stageGate && deal?.stage) {
      const cur  = STAGE_RANK[String(deal.stage).toLowerCase()];
      const gate = STAGE_RANK[String(d.stageGate).toLowerCase()];
      if (cur != null && gate != null && cur < gate) {
        status = "Not yet required";
      }
    }
    // BSA applicability unknown: list the row, never count it as missing or confirmed.
    if (status === "Missing" && d.cladding && bsa === "unknown") status = "Pending BSA confirmation";

    return {
      ...d,
      category: categoryOf(d.id),
      status,
      uploaded_at: upMatch?.uploaded_at || upMatch?.created_at || null,
      uploaded_by: upMatch?.uploaded_by_name || upMatch?.uploaded_by || null,
      uploaded_by_id: upMatch?.uploaded_by_id || null,
      uploaded_by_role: upMatch?.uploaded_by_role || null,
      upload_source: upMatch?.upload_source || null,
      uploaded_id: upMatch?.id || null,
      size: upMatch?.size || null,
      // iter-alignment · P0 · Bulk Assign Reviewer
      // Surface the canonical `assignee_*` fields on the
      // derived row so the table + drawer read them from the
      // SAME upload record — no parallel state.  These are
      // populated when the row's `status === "Needs Review"`
      // (review_status = `needs_review` on the upload) and
      // an operator has assigned a reviewer.
      assignee_id: upMatch?.assignee_id || null,
      assignee_name: upMatch?.assignee_name || null,
      // Full underlying upload record — used by the preview
      // drawer's Details / Linked tabs (file type, mime, size).
      _upload: upMatch || null,
      _request: reqMatch || null,
    };
  });

  // Uploads that match no required-document row (e.g. any file added from the Deal Room) are still
  // this deal's documents — list them under their filed folder instead of dropping them.
  const matched = new Set(rows.map((r) => r.uploaded_id).filter(Boolean));
  uploadedNorm.filter((u) => u.id && !matched.has(u.id)).forEach((u) => {
    const rs = String(u.review_status || u.status || "").toLowerCase();
    rows.push({
      id: `upload:${u.id}`,
      label: u.name || "Document",
      responsible: "",
      icon: FileText,
      group: "uploaded",
      keywords: [],
      category: LIBRARY_CATEGORIES.includes(u.folder) ? u.folder : (/contract|legal|title/i.test(u.folder || "") ? "Legal" : "Property"),
      status: rs === "approved" ? "Approved" : (rs === "reviewed" || rs === "under_review") ? "Reviewed" : "Needs Review",
      uploaded_at: u.uploaded_at || u.created_at || null,
      uploaded_by: u.uploaded_by_name || u.uploaded_by || null,
      uploaded_by_id: u.uploaded_by_id || null,
      uploaded_by_role: u.uploaded_by_role || null,
      upload_source: u.upload_source || null,
      uploaded_id: u.id,
      size: u.size || null,
      assignee_id: u.assignee_id || null,
      assignee_name: u.assignee_name || null,
      _upload: u,
      _request: null,
    });
  });

  return rows;
}

// ═══════════════════════════════════════════════════════════════════
//  Page
// ═══════════════════════════════════════════════════════════════════
export default function DocumentsPage() {
  const { user } = useAuth();
  const {
    deals, findProperty, findContact,
    // iter-alignment · P2 · Document Centre Property Selector
    // (2026-02-28) — pull the SAME canonical `properties` slice
    // the Properties page reads so the selector cannot drift.
    // `propertiesLoading` powers the calm loading state; there
    // is no separate hard-coded property list any more.
    properties: storeProperties, propertiesLoading, dealsHydrated,
    getPilotDocumentRequests, isPropertyArchived,
    removePilotDocument, replacePilotDocument, appendPilotDocument,
  } = useChainStore();
  // Pilot Readiness UX Sweep — mirror the backend noise regex
  // used by Command Centre's Today's Movement filter so TEST_,
  // audit and smoke-test deals never surface in the Document
  // Centre property selector, missing-doc aggregate, request
  // counts or the left rail.  Kept in lockstep with
  // `_NOISE_DEAL_NAME_RE` in `/app/backend/routes_today.py`.
  const _NOISE_DEAL_NAME_RE = /(?:^(?:TEST_|test_|Test iter|TestDeal_|SmokeTest_|smoke[_ ]test)|Audit Deal|V2 Audit|Test Avenue|Mongo legacy deal|DocFreeholdDeal|SmokeTest|\bTEST_)/i;
  const _isNoiseDealName = (name) => !!(name && _NOISE_DEAL_NAME_RE.test(String(name).trim()));

  // iter-290 — Deals on archived properties drop out of the active
  // Document Centre surfaces (selectors, missing-doc aggregate,
  // pending-request counts). Direct Deal Room URLs still resolve.
  const activeDeals = useMemo(
    () => ((deals || []).filter((d) =>
      !d.archived
      && d.status !== "Completed"
      && d.status !== "Fallen Through"
      && !isPropertyArchived?.(d.propertyId || d.property_id)
      && !_isNoiseDealName(d.name)
      && !_isNoiseDealName(findProperty?.(d.propertyId || d.property_id)?.address),
    )
  ), [deals, isPropertyArchived, findProperty]);

  /* iter-alignment · P2 · Document Centre Property Selector
   * (2026-02-28)
   * --------------------------------------------------------------
   * The dropdown is now sourced from the SAME `properties` slice
   * the Properties page reads (`storeProperties`) with the SAME
   * visibility rules (`isPropertyArchived`, noise-name regex).
   * The old deal-first selector has been retired.
   *
   *   • Selector value  = `property.id` (never the address).
   *   • Loading state   = powered by `propertiesLoading`.
   *   • Empty / error   = calm placeholder (no rows to select).
   *   • Persistence     = `chainflow:doc-centre:selected-property`
   *     in localStorage so the operator's choice survives cross-
   *     page navigation.  Deep-links (`/documents/:dealId`) still
   *     win and override the stored preference on that mount.
   *   • Downstream      = the property → active-deal lookup
   *     resolves the deal record the existing document / request
   *     fetchers depend on, so no downstream code needs changing.
   */
  const visibleProperties = useMemo(() => {
    const rows = (storeProperties || []).filter((p) => (
      p && p.id
      && !isPropertyArchived?.(p.id)
      && !_isNoiseDealName(p.address)
    ));
    return rows;
  }, [storeProperties, isPropertyArchived]);
  const dealByPropertyId = useMemo(() => {
    const map = new Map();
    (activeDeals || []).forEach((d) => {
      const pid = d.propertyId || d.property_id;
      if (pid && !map.has(pid)) map.set(pid, d);
    });
    return map;
  }, [activeDeals]);

  const _SELECTED_PROP_KEY = "chainflow:doc-centre:selected-property";
  const { dealId: routeDealId } = useParams();
  const navigate = useNavigate();
  const [selectedPropertyId, setSelectedPropertyId] = useState(() => {
    // A deep link decides the property once deals load; the saved one must not show first.
    if (typeof window === "undefined" || routeDealId) return null;
    try { return window.localStorage.getItem(_SELECTED_PROP_KEY) || null; } catch { return null; }
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (selectedPropertyId) window.localStorage.setItem(_SELECTED_PROP_KEY, selectedPropertyId);
    } catch { /* noop */ }
  }, [selectedPropertyId]);

  const [dealId, setDealId] = useState(null);
  // Deal Room deep-link (`/documents/:dealId`) — pre-select the
  // property so the operator lands directly on its Library.  We
  // wait until `activeDeals` has hydrated so the ID resolves to
  // a real deal record before we set it.
  // A direct deep link also resolves completed deals (they leave only the active selectors).
  const routeDeal = useMemo(
    () => (routeDealId ? (deals || []).find((d) => d.id === routeDealId && !d.archived) || null : null),
    [deals, routeDealId],
  );
  // Apply the deep link once per URL — store refreshes must not pull the operator back off a deal they chose.
  const _appliedRouteDeal = useRef(null);
  useEffect(() => {
    if (!routeDealId || (activeDeals.length === 0 && !routeDeal)) return;
    if (_appliedRouteDeal.current === routeDealId) return;
    const match = activeDeals.find((d) => d.id === routeDealId) || routeDeal;
    if (match) {
      _appliedRouteDeal.current = routeDealId;
      setDealId(match.id);
      setSelectedPropertyId(match.propertyId || match.property_id || null);
    }
  }, [routeDealId, activeDeals, routeDeal]);
  // An unknown deal id: once deals have loaded, drop it from the URL so the saved property is restored.
  useEffect(() => {
    if (!routeDealId || !dealsHydrated || (deals || []).length === 0) return;
    if (routeDeal || activeDeals.some((d) => d.id === routeDealId)) return;
    toast.error("That deal couldn't be found", { id: "doc-centre-deal-not-found", description: "Showing your Document Centre instead." });
    let saved = null;
    try { saved = window.localStorage.getItem(_SELECTED_PROP_KEY); } catch { /* noop */ }
    _appliedRouteDeal.current = null;
    if (saved) setSelectedPropertyId(saved);
    navigate("/documents", { replace: true });
  }, [routeDealId, dealsHydrated, routeDeal, activeDeals, deals, navigate]);
  // iter-alignment · P2 · Document Centre Property Selector —
  // Default resolution order:
  //   1. Route deep-link `/documents/:dealId` (handled above).
  //   2. Persisted `selectedPropertyId` (if it still resolves).
  //   3. First property in the visible list (Properties-page order).
  // Deals on the chosen property drive the downstream fetch.
  useEffect(() => {
    if (dealId || visibleProperties.length === 0) return;
    // A resolvable deep link owns the selection; the persisted property must not override it.
    // A deal in the URL is handled by the deep-link / unknown-deal effects; never pick a default here.
    if (routeDealId) return;
    const persistedProp = selectedPropertyId
      ? visibleProperties.find((p) => p.id === selectedPropertyId)
      : null;
    const anchorProp = persistedProp || visibleProperties[0];
    if (!anchorProp) return;
    if (!selectedPropertyId || selectedPropertyId !== anchorProp.id) {
      setSelectedPropertyId(anchorProp.id);
    }
    const anchorDeal = dealByPropertyId.get(anchorProp.id);
    if (anchorDeal) setDealId(anchorDeal.id);
  }, [visibleProperties, dealByPropertyId, dealId, selectedPropertyId, routeDealId]);

  // Property switch handler for the selector — resolves the
  // property's active deal so the documents panel refreshes for
  // the operator's choice.  Called with a canonical property id.
  const onPropertyChange = useCallback((pid) => {
    if (!pid) return;
    try { window.localStorage.setItem(_SELECTED_PROP_KEY, pid); } catch { /* noop */ }
    setSelectedPropertyId(pid);
    const d = dealByPropertyId.get(pid);
    setDealId(d ? d.id : null);
    // Keep the URL on the deal being shown so refresh / share never reopens the previous deal.
    if (d) _appliedRouteDeal.current = d.id;
    const target = d ? `/documents/${d.id}` : "/documents";
    if ((routeDealId || null) !== (d ? d.id : null)) navigate(target, { replace: true });
  }, [dealByPropertyId, navigate, routeDealId]);

  const deal = useMemo(
    () => activeDeals.find((d) => d.id === dealId) || (routeDeal && routeDeal.id === dealId ? routeDeal : null),
    [activeDeals, dealId, routeDeal],
  );
  // The heading always describes the deal whose documents are shown; a persisted selection for
  // another property never relabels them.
  const property = useMemo(() => {
    const pid = deal ? (deal.propertyId || deal.property_id) : selectedPropertyId;
    if (!pid) return deal ? findProperty(deal.propertyId) : null;
    return (storeProperties || []).find((x) => x.id === pid) || findProperty(pid) || null;
  }, [selectedPropertyId, storeProperties, deal, findProperty]);

  const [documents, setDocuments] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all | missing | uploaded | approved | blocked
  const [uploading, setUploading] = useState(false);
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);
  const fileInputRef = useRef(null);
  const customInputRef = useRef(null);
  const liveActivity = useDealActivity(deal?.id, { refreshKey: activityRefreshKey });

  // Only the latest deal's response may populate the panel — a slow reply for a previous deal is dropped.
  const _currentDealRef = useRef(null);
  const [docsLoadFailed, setDocsLoadFailed] = useState(false);
  const _apiDocIdsRef = useRef(new Set());
  useEffect(() => {
    _currentDealRef.current = deal?.id || null;
    setDocuments([]);
    setRequests([]);
    setDocsLoadFailed(false);
  }, [deal?.id]);
  const refetch = useCallback(async () => {
    if (!deal?.id) return;
    const forDeal = deal.id;
    setLoading(true);
    try {
      let failed = false;
      const [docsResp, reqResp] = await Promise.all([
        api.get(`/api/deals/${deal.id}/documents`).catch(() => { failed = true; return { data: [] }; }),
        api.get(`/api/deals/${deal.id}/document-requests`).catch(() => ({ data: [] })),
      ]);
      if (_currentDealRef.current === forDeal) setDocsLoadFailed(failed);
      const apiDocs = Array.isArray(docsResp.data) ? docsResp.data : [];
      const apiReqs = Array.isArray(reqResp.data) ? reqResp.data : [];
      // iter-270I — Safe fallback for pilot deals (frontend-only).
      // The pilot deal carries seeded documents + the chainStore
      // holds locally-composed pending requests. Both fold in only
      // when their API counterpart returned empty so we never
      // double-count when a real backend deal is present.
      const fallbackDocs = (apiDocs.length === 0 && Array.isArray(deal?.documents))
        ? deal.documents
        : [];
      const pilotReqs = (typeof getPilotDocumentRequests === "function")
        ? (getPilotDocumentRequests(deal.id) || [])
        : [];
      const apiIds = new Set(apiReqs.map((r) => r.id));
      const mergedReqs = [
        ...apiReqs,
        ...pilotReqs.filter((r) => r && r.id && !apiIds.has(r.id)),
      ];
      if (_currentDealRef.current !== forDeal) return;
      // Docs the server returned are real records — delete/replace must go through the API for them.
      _apiDocIdsRef.current = new Set(apiDocs.map((x) => String(x.id)));
      setDocuments([...apiDocs, ...fallbackDocs]);
      setRequests(mergedReqs);
    } finally {
      if (_currentDealRef.current === forDeal) setLoading(false);
    }
  }, [deal?.id, deal?.documents, getPilotDocumentRequests]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // ─── Workspace-wide summary (across active deals) ───────────────
  // We compute this per active deal using the derived rows, then sum
  // counts. Some deals may not have a Mongo document collection
  // (Postgres-native v2 deals); their fetch returns 404 which we
  // swallow calmly — the summary degrades by simply showing fewer
  // counts, never an error.
  const [allDealsDocs, setAllDealsDocs] = useState({}); // dealId → uploaded docs
  // iter-288 — Load real document-requests per active deal so the
  // aggregate "Missing Documents" count is computed against actual
  // pending requests (not the previous hardcoded `requests: []`,
  // which inflated the missing total — e.g. the bogus aggregate
  // counts surfaced before this iteration).
  // Pilot deals fold in via `getPilotDocumentRequests`; live deals
  // hit `/api/deals/{id}/document-requests`. A 404 (Postgres-native
  // deal with no Mongo doc-requests collection) is swallowed
  // calmly — the loader's `ready` flag below ensures the badge is
  // simply hidden until we have data we trust.
  // Probes re-run only when the set of deals changes, not on every store re-render (that flood caused 429s).
  const activeDealIdsKey = activeDeals.map((d) => d.id).join(",");
  const _activeDealsRef = useRef(activeDeals);
  _activeDealsRef.current = activeDeals;
  const [allDealsRequests, setAllDealsRequests] = useState({});
  const [allDealsRequestsReady, setAllDealsRequestsReady] = useState(false);
  useEffect(() => {
    const activeDeals = _activeDealsRef.current;
    if (activeDeals.length === 0) { setAllDealsRequestsReady(true); return; }
    let cancelled = false;
    (async () => {
      const map = {};
      await Promise.all(
        activeDeals.slice(0, 24).map(async (d) => {
          try {
            const r = await api.get(`/api/deals/${d.id}/document-requests`, {
              _suppressErrorToast: true,
            });
            const api_rows = Array.isArray(r.data) ? r.data : [];
            const pilot_rows = getPilotDocumentRequests?.(d.id) || [];
            // De-dupe by id; API rows win when both present.
            const seen = new Set(api_rows.map((x) => x.id));
            map[d.id] = [...api_rows, ...pilot_rows.filter((x) => !seen.has(x.id))];
          } catch {
            map[d.id] = getPilotDocumentRequests?.(d.id) || [];
          }
        }),
      );
      if (!cancelled) { setAllDealsRequests(map); setAllDealsRequestsReady(true); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDealIdsKey]);

  useEffect(() => {
    const activeDeals = _activeDealsRef.current;
    if (activeDeals.length === 0) return;
    let cancelled = false;
    (async () => {
      const map = {};
      await Promise.all(
        activeDeals.slice(0, 12).map(async (d) => {
          try {
            const r = await api.get(`/api/deals/${d.id}/documents`, {
              // Mute the global error toast for these probe calls.
              _suppressErrorToast: true,
            });
            map[d.id] = Array.isArray(r.data) ? r.data : [];
          } catch {
            map[d.id] = [];
          }
        }),
      );
      if (!cancelled) setAllDealsDocs(map);
    })();
    return () => { cancelled = true; };
  }, [activeDealIdsKey]);

  /* iter-alignment · P0 · Bulk Assign Reviewer
     ------------------------------------------------------------
     Fetch the organisation's members once per session; the same
     canonical `/api/team/members` endpoint used by Settings →
     Team is our SSOT for reviewer identity.  Zero hardcoded
     reviewers.  Silent-failure allowed — an empty picker just
     disables the Assign action, no rendering break. */
  const [orgMembers, setOrgMembers] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/api/team/members", { _suppressErrorToast: true });
        if (!cancelled) setOrgMembers(Array.isArray(r.data) ? r.data : []);
      } catch {
        if (!cancelled) setOrgMembers([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const summary = useMemo(() => {
    let missing = 0, awaitingReview = 0, approved = 0, blocking = 0;
    for (const d of activeDeals) {
      // iter-288 — Fold real document-requests into the derivation
      // so the aggregate count reflects the true outstanding work
      // (a doc that has been requested but not yet fulfilled is
      // surfaced as `Requested`, not `Missing`). Only count rows
      // whose stage gate has been reached — same operational
      // discipline the workspace view already applies — so the
      // badge counts work that's *actually* slowing exchange.
      const rows = deriveRows({
        tenure: d.tenure,
        deal: d,
        uploaded: allDealsDocs[d.id] || [],
        requests: allDealsRequests[d.id] || [],
      });
      const cur = STAGE_RANK[d.stage] ?? 0;
      for (const r of rows) {
        if (r.status === "Missing") {
          const gate = STAGE_RANK[r.stageGate] ?? 99;
          if (cur >= gate) {
            missing += 1;
            blocking += 1;
          }
        }
        if (r.status === "Reviewed" || r.status === "Needs Review") awaitingReview += 1;
        if (r.status === "Approved") approved += 1;
      }
    }
    return { missing, awaitingReview, approved, blocking };
  }, [activeDeals, allDealsDocs, allDealsRequests]);

  // ─── Per-deal derived rows ──────────────────────────────────────
  const rows = useMemo(
    () => deriveRows({ tenure: deal?.tenure, deal, uploaded: documents, requests }),
    [deal, documents, requests],
  );

  const coreRows = rows.filter((r) => r.group === "core");
  let additionalRows = rows.filter((r) => r.group === "additional");
  // Show additional rows that have any signal (uploaded / requested /
  // status not Missing) up front, plus the top 4 leasehold/compliance
  // missing — keeps the additional list calm and scannable.
  const additionalActive = additionalRows.filter((r) => r.status !== "Missing");
  const additionalMissing = additionalRows.filter((r) => r.status === "Missing").slice(0, 4);
  additionalRows = [...additionalActive, ...additionalMissing];

  const _requiredRows = rows.filter((r) => r.group !== "uploaded");  // completeness = required documents only
  const completeCount = _requiredRows.filter((r) => r.status === "Approved" || r.status === "Reviewed" || r.status === "Needs Review").length;
  const totalCount = _requiredRows.length;
  const pct = totalCount ? Math.round((completeCount / totalCount) * 100) : 0;

  // ─── Operational blockers ───────────────────────────────────────
  // We also overlay `lastChasedAt` per blocker by scanning the live
  // activity feed for chase events. Backend writes Mongo `kind=chase`
  // events AND Postgres-mirrored `reminder_sent` activities whose
  // title begins with "Chase sent · {doc.label}". Both shapes are
  // recognised here so the calm "Chased Xm ago" state survives
  // refresh + appears immediately in the operational rail.
  const chaseTimestamps = useMemo(() => {
    const map = new Map();
    const setIfNewer = (k, at) => {
      if (!k || !at) return;
      const prev = map.get(k);
      if (!prev || at > prev) map.set(k, at);
    };
    for (const a of liveActivity || []) {
      const kind = String(a.activity_type || a.kind || "").toLowerCase();
      const title = String(a.title || "");
      const at = a.created_at || a.at;
      const refId = a.ref_item_id || a.meta?.item_id || a.meta?.ref_item_id;

      // Direct Mongo chase shape with ref_item_id pointer.
      if ((kind === "chase" || kind === "chase_logged") && refId) {
        setIfNewer(refId, at);
        continue;
      }
      // Postgres-mirrored `reminder_sent` chase — recognise by title.
      if (kind === "reminder_sent" && title.startsWith("Chase sent · ")) {
        const label = title.replace(/^Chase sent · /, "");
        // Reverse-lookup the canonical doc id by label.
        const match = REQUIRED_DOCS.find((d) => d.label === label);
        if (match) setIfNewer(match.id, at);
      }
    }
    return map;
  }, [liveActivity]);

  const blockers = useMemo(() => {
    const cur = STAGE_RANK[deal?.stage] ?? 0;
    const targetExchange = deal?.targetExchange || deal?.target_exchange;
    let daysToExchange = null;
    if (targetExchange) {
      const t = Date.parse(targetExchange);
      if (!Number.isNaN(t)) daysToExchange = Math.floor((t - Date.now()) / 86_400_000);
    }
    return rows
      .filter((r) => r.status === "Missing" || r.status === "Blocked")
      .map((r) => {
        const gate = STAGE_RANK[r.stageGate] ?? 99;
        const overGate = cur >= gate;
        const daysFromStageGate = Math.max(0, (cur - gate) * 3); // calm proxy: 3d per stage past gate
        // Backend blocker_engine maps statuses to severity differently —
        // here we mirror its rule: Blocked → blocking, otherwise warning.
        const severity = r.status === "Blocked" ? "blocking" : (daysFromStageGate >= 7 ? "warning" : "info");
        const ladder = computeRung({
          blockerId: r.id,
          blockerTitle: r.label,
          severity,
          activities: liveActivity || [],
          daysToExchange,
        });
        return {
          ...r,
          urgency: r.status === "Blocked" ? "High" : overGate ? (daysFromStageGate >= 7 ? "Medium" : "Medium") : "Low",
          days: daysFromStageGate,
          overGate,
          severity,
          lastChasedAt: chaseTimestamps.get(r.id) || ladder.last_chase_at || null,
          ladder,
        };
      })
      .filter((b) => b.overGate)
      .sort((a, b) => (a.urgency === "High" ? -1 : 1) - (b.urgency === "High" ? -1 : 1) || b.days - a.days)
      .slice(0, 4);
  }, [rows, deal?.stage, deal?.targetExchange, deal?.target_exchange, chaseTimestamps, liveActivity]);

  // ─── Filtered rows for required-docs list ───────────────────────
  const filteredCore = useMemo(() => {
    if (filter === "all") return coreRows;
    if (filter === "missing")   return coreRows.filter((r) => r.status === "Missing");
    if (filter === "uploaded")  return coreRows.filter((r) => r.status === "Needs Review" || r.status === "Reviewed");
    if (filter === "approved")  return coreRows.filter((r) => r.status === "Approved");
    if (filter === "blocked")   return coreRows.filter((r) => r.status === "Blocked");
    return coreRows;
  }, [coreRows, filter]);

  // ─── Upload handlers ────────────────────────────────────────────
  const onUpload = useCallback(async (files, tag = null) => {
    if (!deal?.id || !files?.length) return;
    setUploading(true);
    const arr = Array.from(files);
    let succeeded = 0;
    // iter-336/Δ — Pilot deals don't exist in the backend, so the
    // canonical POST 404s. Route the upload through the pilot
    // helper instead — it persists via localStorage, logs a calm
    // activity row, and fires the Docs → Milestone cascade.
    const isPilotDeal = deal?._pilot === true || !!deal?._pilotOverridden
                     || (Array.isArray(deal?.documents) && deal.documents.some((d) => d?._pilot));
    try {
      for (const f of arr) {
        // Client preflight — instant, calm feedback before bytes hit the wire.
        const pre = clientPreflight(f);
        if (!pre.ok) {
          toast.error(`${pre.title} · ${f.name}`, { description: pre.reason });
          continue;
        }
        if (isPilotDeal) {
          appendPilotDocument?.(deal.id, f);
          succeeded += 1;
          continue;
        }
        const fd = new FormData();
        fd.append("file", f);
        if (tag) fd.append("tag", tag);
        try {
          await api.post(`/api/deals/${deal.id}/documents`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          succeeded += 1;
        } catch (err) {
          const { title, description } = uploadErrorPresentation(err, f.name);
          toast.error(title, { description });
        }
      }
      if (succeeded > 0) {
        toast.success(
          succeeded === arr.length
            ? `Uploaded ${succeeded} file${succeeded === 1 ? "" : "s"}`
            : `Uploaded ${succeeded} of ${arr.length} files`,
        );
        invalidateDealActivity(deal.id);
        await refetch();
      }
    } finally {
      setUploading(false);
    }
  }, [deal, refetch, appendPilotDocument]);

  const onRequest = useCallback(async (row) => {
    if (!deal?.id) return;
    try {
      await api.post(`/api/deals/${deal.id}/document-requests`, {
        doc_type: row.label,
        title: `Request · ${row.label}`,
      });
      toast.success(`${row.label} requested`);
      invalidateDealActivity(deal.id);
      await refetch();
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not create request";
      toast.error(typeof msg === "string" ? msg : "Could not create request");
    }
  }, [deal?.id, refetch]);

  /* iter-alignment · P0 · Bulk Request From Library
   *
   * Fires one request per selected row against the SAME
   * `POST /api/deals/{id}/document-requests` endpoint the
   * single-row action uses.  No new endpoint, no parallel
   * request state.  `Promise.allSettled` isolates partial
   * failures so a mid-batch API error never rolls back the
   * successful requests (the user's explicit ask).
   *
   * Eligibility is enforced at CALL time too (defence-in-depth
   * against a stale selection): only rows whose derived
   * `status === "Missing"` survive.  Duplicate protection
   * comes for free from the backend request endpoint (the
   * Mongo document-requests collection dedupes on `doc_type`
   * per deal) — we still surface any per-row error via toast.
   */
  const onBulkRequest = useCallback(async (rowsToRequest) => {
    if (!deal?.id) return { ok: 0, fail: 0 };
    const eligible = (rowsToRequest || []).filter((r) => r && r.status === "Missing");
    if (eligible.length === 0) {
      toast("No missing documents in the current selection to request.");
      return { ok: 0, fail: 0 };
    }
    const outcomes = await Promise.allSettled(
      eligible.map((r) =>
        api.post(`/api/deals/${deal.id}/document-requests`, {
          doc_type: r.label,
          title: `Request · ${r.label}`,
        }),
      ),
    );
    const ok = outcomes.filter((o) => o.status === "fulfilled").length;
    const fail = outcomes.length - ok;
    if (ok > 0) {
      invalidateDealActivity(deal.id);
      await refetch();
    }
    if (fail === 0) {
      toast.success(`Requested ${ok} document${ok === 1 ? "" : "s"}`);
    } else if (ok === 0) {
      const firstErr = outcomes.find((o) => o.status === "rejected")?.reason;
      const msg = firstErr?.response?.data?.detail?.message
              || firstErr?.response?.data?.detail
              || "Could not create requests";
      toast.error(typeof msg === "string" ? msg : "Could not create requests");
    } else {
      toast.warning(`Requested ${ok} · ${fail} failed`, {
        description: "Successful requests are saved. Try the failed rows again from the row action.",
      });
    }
    return { ok, fail };
  }, [deal?.id, refetch]);

  /* iter-alignment · P0 · Bulk Assign Reviewer
     ------------------------------------------------------------
     Fires ONE `POST /api/deals/{id}/documents/{doc}/review`
     per selected Needs Review row with
     `action="assign_reviewer"` + `assignee_id`.  This is the
     same endpoint the single-row Mark Reviewed / Request
     Replacement flows use — no new route, no new state, no
     parallel taxonomy.  `Promise.allSettled` isolates partial
     failures.  Returns `{ ok, fail, failedIds }` so the caller
     can KEEP the failed rows selected for retry (per the ask).
   */
  const onBulkAssign = useCallback(async (rowsToAssign, assignee) => {
    if (!deal?.id || !assignee?.user_id) return { ok: 0, fail: 0, failedIds: [] };
    const eligible = (rowsToAssign || []).filter(
      (r) => r && r.status === "Needs Review" && !!r.uploaded_id,
    );
    if (eligible.length === 0) {
      toast("No documents awaiting review in the current selection.");
      return { ok: 0, fail: 0, failedIds: [] };
    }
    const outcomes = await Promise.allSettled(
      eligible.map((r) =>
        api.post(
          `/api/deals/${deal.id}/documents/${r.uploaded_id}/review`,
          { action: "assign_reviewer", assignee_id: assignee.user_id },
        ),
      ),
    );
    const okRows = [];
    const failRows = [];
    outcomes.forEach((o, i) => {
      if (o.status === "fulfilled") okRows.push(eligible[i]);
      else                          failRows.push(eligible[i]);
    });
    if (okRows.length > 0) {
      invalidateDealActivity(deal.id);
      await refetch();
    }
    const displayName = assignee.full_name || assignee.email || "Reviewer";
    if (failRows.length === 0) {
      toast.success(`Assigned ${okRows.length} document${okRows.length === 1 ? "" : "s"} to ${displayName}`);
    } else if (okRows.length === 0) {
      const firstErr = outcomes.find((o) => o.status === "rejected")?.reason;
      const msg = firstErr?.response?.data?.detail?.message
              || firstErr?.response?.data?.detail
              || "Could not assign reviewer";
      toast.error(typeof msg === "string" ? msg : "Could not assign reviewer");
    } else {
      toast.warning(`Assigned ${okRows.length} · ${failRows.length} failed`, {
        description: `${displayName} was set on the successful rows. The failed ones are still selected — try again.`,
      });
    }
    return { ok: okRows.length, fail: failRows.length, failedIds: failRows.map((r) => r.id) };
  }, [deal?.id, refetch]);

  const onPreview = useCallback(async (row) => {
    if (!deal?.id || !row?.uploaded_id) return;
    try {
      const r = await api.get(`/api/deals/${deal.id}/documents/${row.uploaded_id}/url`);
      if (r.data?.url) window.open(r.data.url, "_blank", "noopener");
    } catch {
      toast.error("Could not open document");
    }
  }, [deal?.id]);

  /* iter-336 — Document operational upgrade.
   *
   * The whole row is now clickable and opens a calm right-aligned
   * preview drawer carrying View / Download / Replace / Delete
   * actions. State lives at the page level so a single drawer
   * instance is reused across the Required + Additional lists. */
  const [previewRow, setPreviewRow] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const replaceInputRef = useRef(null);

  // Keep `previewRow` in sync with the latest `rows` so the drawer
  // reflects upload/replace/delete results without manual closure.
  const refreshPreviewFromRows = useCallback((rowsList) => {
    setPreviewRow((cur) => {
      if (!cur) return cur;
      const next = (rowsList || []).find((r) => r.id === cur.id);
      return next || null;
    });
  }, []);

  const onDelete = useCallback(async (row) => {
    if (!deal?.id || !row?.uploaded_id) return;
    setDeleting(true);
    try {
      const docId = row.uploaded_id;
      // Pilot docs are the ones the API did not return; real records always go through the API.
      const isPilotDoc = !_apiDocIdsRef.current.has(String(docId));
      if (isPilotDoc) {
        removePilotDocument?.(deal.id, docId);
      } else {
        await api.delete(`/api/deals/${deal.id}/documents/${docId}`);
      }
      toast.success(`${row.label} removed`);
      invalidateDealActivity(deal.id);
      setConfirmingDelete(false);
      setPreviewRow(null);
      await refetch();
    } catch (e) {
      const msg = e?.response?.data?.detail?.message
              || e?.response?.data?.detail
              || "Could not remove document";
      toast.error(typeof msg === "string" ? msg : "Could not remove document");
    } finally {
      setDeleting(false);
    }
  }, [deal?.id, refetch, removePilotDocument]);

  const onReplace = useCallback(async (row, file) => {
    if (!deal?.id || !row?.uploaded_id || !file) return;
    const docId = row.uploaded_id;
    // Same source check as `onDelete`.
    const isPilotDoc = !_apiDocIdsRef.current.has(String(docId));
    const pre = clientPreflight(file);
    if (!pre.ok) {
      toast.error(`${pre.title} · ${file.name}`, { description: pre.reason });
      return;
    }
    setReplacing(true);
    try {
      if (isPilotDoc) {
        replacePilotDocument?.(deal.id, docId, file);
      } else {
        /* iter-alignment · Doc Attribution (2026-09) — replacement
         *   history.  The server now appends a `replaced`
         *   review_history event (with the prior doc's {id, name,
         *   size, uploaded_at, uploaded_by_name}) when we pass
         *   `replaces=<prev_id>`.  Delete of the superseded row
         *   is left to the caller — we skip it so the audit
         *   chain stays intact on the old doc too, and the new
         *   doc carries the replacement pointer. */
        const fd = new FormData();
        fd.append("file", file);
        if (row.id) fd.append("tag", row.id);
        fd.append("replaces", docId);
        await api.post(`/api/deals/${deal.id}/documents`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`${row.label} replaced`);
      invalidateDealActivity(deal.id);
      await refetch();
    } catch (e) {
      const { title, description } = uploadErrorPresentation(e, file.name);
      toast.error(title, { description });
    } finally {
      setReplacing(false);
    }
  }, [deal?.id, refetch, replacePilotDocument]);

  /* iter-alignment · Doc Attribution (2026-09) — direct upload
   * from the preview-panel dropzone.  Reuses the same
   * `/api/deals/{id}/documents` route + `tag=row.id` field so
   * the doc lands against the correct required slot, then
   * optimistically patches `previewRow` so the drawer flips
   * Missing → Needs Review WITHOUT a full refetch — attribution
   * (uploaded_at + uploaded_by_*) comes straight from the
   * server response so refresh persistence is intact. */
  const onDropzoneUpload = useCallback(async (row, file) => {
    if (!deal?.id || !row || !file) return;
    const pre = clientPreflight(file);
    if (!pre.ok) {
      toast.error(`${pre.title} · ${file.name}`, { description: pre.reason });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (row.id) fd.append("tag", row.id);
      const resp = await api.post(`/api/deals/${deal.id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const doc = resp?.data || {};
      // Local optimistic patch — flip Missing → Needs Review
      // without waiting on refetch.  The full attribution
      // payload lands in the same response.
      setPreviewRow((cur) => cur ? ({
        ...cur,
        status: "Needs Review",
        uploaded_id: doc.id || cur.uploaded_id,
        uploaded_at: doc.uploaded_at || doc.created_at || new Date().toISOString(),
        uploaded_by: doc.uploaded_by_name || cur.uploaded_by || null,
        uploaded_by_id: doc.uploaded_by_id || cur.uploaded_by_id || null,
        uploaded_by_role: doc.uploaded_by_role || cur.uploaded_by_role || null,
        upload_source: doc.upload_source || "manual",
        _upload: doc,
      }) : cur);
      toast.success(`${row.label} uploaded`);
      invalidateDealActivity(deal.id);
      // Refetch in the background so the underlying rows list +
      // Deal Room documents card + activity timeline all read
      // the same canonical attribution.
      refetch();
    } catch (e) {
      const { title, description } = uploadErrorPresentation(e, file.name);
      toast.error(title, { description });
    } finally {
      setUploading(false);
    }
  }, [deal?.id, refetch]);

  // iter-336 — Keep the preview drawer's `row` in sync with the
  // latest derivation pass so upload / replace / delete results
  // flow through without manual closure tracking.
  useEffect(() => {
    refreshPreviewFromRows(rows);
  }, [rows, refreshPreviewFromRows]);

  // ─── Tabs ───────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("overview"); // overview | missing | all | requests

  // Requests log across active deals
  const [allRequests, setAllRequests] = useState([]);
  useEffect(() => {
    if (activeTab !== "requests" || activeDeals.length === 0) return;
    let cancelled = false;
    (async () => {
      const all = [];
      await Promise.all(
        activeDeals.slice(0, 12).map(async (d) => {
          // iter-270I — Unified document request feed.
          // 1) Pull API-side requests (real backend deals).
          try {
            const r = await api.get(`/api/deals/${d.id}/document-requests`);
            const items = Array.isArray(r.data) ? r.data : [];
            for (const it of items) all.push({ ...it, _deal: d });
          } catch { /* noop */ }
          // 2) Merge pilot requests (frontend-only deals + locally-
          //    composed pilot requests). De-duplicated against API
          //    rows by `id` so we never double-count.
          try {
            const pilot = (typeof getPilotDocumentRequests === "function")
              ? getPilotDocumentRequests(d.id) || []
              : [];
            const seenIds = new Set(all.filter((x) => x._deal?.id === d.id).map((x) => x.id));
            for (const it of pilot) {
              if (it && it.id && !seenIds.has(it.id)) {
                all.push({ ...it, _deal: d });
              }
            }
          } catch { /* fail soft */ }
        })
      );
      if (!cancelled) {
        all.sort((a, b) => Date.parse(b.created_at || 0) - Date.parse(a.created_at || 0));
        setAllRequests(all);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, activeDeals, getPilotDocumentRequests]);


  // ─── Render ─────────────────────────────────────────────────────
  return (
    <div className="min-h-screen" style={{ background: "#FAFAFC" }}>
      <Sidebar />
      <main
        className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out min-h-screen overflow-x-hidden"
        data-testid="documents-hub-main"
      >
        <div className={WORKSPACE_SHELL_CLASS}>
          {/* iter-P0 · Property Transaction Library
              ---------------------------------------------------
              The Document Centre IS the transaction library.
              We render a single, adapted surface here (title
              row → property anchor card → 2-column library
              layout).  Every handler, hook and SSOT read used
              below is the same one the old tabs relied on —
              this is a re-layout, not a rewrite. */}
          {docsLoadFailed && (
            <div role="alert" data-testid="doc-centre-load-error"
              className="mb-3 flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900">
              <span>This deal's documents couldn't be loaded, so the list below may be incomplete.</span>
              <button type="button" onClick={refetch} data-testid="doc-centre-load-retry"
                className="font-semibold underline underline-offset-2 hover:text-amber-700">Try again</button>
            </div>
          )}
          <PropertyTransactionLibrary
            deal={deal}
            property={property}
            deals={activeDeals}
            onDealChange={setDealId}
            /* iter-alignment · P2 · Document Centre Property
               Selector — canonical property list + handler.
               Passed straight through to the anchor card so the
               dropdown iterates properties, not deals. */
            properties={visibleProperties}
            selectedPropertyId={selectedPropertyId}
            onPropertyChange={onPropertyChange}
            propertiesLoading={propertiesLoading}
            dealByPropertyId={dealByPropertyId}
            rows={rows}
            loading={loading && documents.length === 0}
            requests={requests}
            previewRow={previewRow}
            onSelectRow={(row) => setPreviewRow(row)}
            onRequest={onRequest}
            onBulkRequest={onBulkRequest}
            onBulkAssign={onBulkAssign}
            orgMembers={orgMembers}
            onUploadClick={() => fileInputRef.current?.click()}
            onUploadForRow={(row) => {
              if (!fileInputRef.current) return;
              fileInputRef.current.dataset.tag = row.id;
              fileInputRef.current.click();
            }}
          />
        </div>

        {/* Hidden inputs for uploads (tagged + free-form) */}
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept="application/pdf,image/png,image/jpeg"
          multiple
          onChange={(e) => {
            const tag = fileInputRef.current?.dataset.tag || null;
            onUpload(e.target.files, tag);
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
              delete fileInputRef.current.dataset.tag;
            }
          }}
          data-testid="documents-hub-file-input"
        />
        <input
          ref={customInputRef}
          type="file"
          className="hidden"
          accept="application/pdf,image/png,image/jpeg"
          multiple
          onChange={(e) => {
            onUpload(e.target.files);
            if (customInputRef.current) customInputRef.current.value = "";
          }}
          data-testid="documents-hub-custom-input"
        />
        {/* iter-336 — Hidden single-file picker used by the
            Replace action inside the preview drawer. */}
        <input
          ref={replaceInputRef}
          type="file"
          className="hidden"
          accept="application/pdf,image/png,image/jpeg"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f && previewRow) onReplace(previewRow, f);
            if (replaceInputRef.current) replaceInputRef.current.value = "";
          }}
          data-testid="documents-hub-replace-input"
        />

        {/* iter-336 — Preview drawer (right-aligned overlay). */}
        <DocumentPreviewDrawer
          row={previewRow}
          open={!!previewRow}
          onClose={() => { setPreviewRow(null); setConfirmingDelete(false); }}
          onView={() => previewRow && onPreview(previewRow)}
          onDownload={() => previewRow && onPreview(previewRow)}
          onReplace={() => replaceInputRef.current?.click()}
          onUpload={onDropzoneUpload}
          onRequestDelete={() => setConfirmingDelete(true)}
          onCancelDelete={() => setConfirmingDelete(false)}
          onConfirmDelete={() => previewRow && onDelete(previewRow)}
          confirmingDelete={confirmingDelete}
          deleting={deleting}
          replacing={replacing}
          uploading={uploading}
        />
      </main>
    </div>
  );
}

/* ─── Header ───────────────────────────────────────────────────── */
/* ═══════════════════════════════════════════════════════════════════
   Property Transaction Library
   ───────────────────────────────────────────────────────────────────
   The visible surface of the Document Centre.  This component is
   a pure re-layout over the same rows / handlers / preview drawer
   the old Documents Hub already exposed — no new API, no new
   storage, no duplicate document records.

   Layout mirrors the approved reference:
     • Title row — brand · request/upload actions
     • Property anchor card — Status · Current Stage · Target Exchange
     • Two-column body:
         LEFT  · Library sub-nav (filter chips + counts)
                 · "Can't find what you need?" request tile
         MAIN  · Search bar + Filter dropdown
                 · Documents table (Document · Category · Stage
                   · Uploaded · Added by · Status)
     • Right drawer opens on row click (Preview · Details ·
       Activity · Linked).  When closed the table reclaims the
       full width.
   ═══════════════════════════════════════════════════════════════ */
function PropertyTransactionLibrary({
  deal, property, deals, onDealChange,
  // iter-alignment · P2 · Document Centre Property Selector
  properties, selectedPropertyId, onPropertyChange, propertiesLoading, dealByPropertyId,
  rows, loading, requests,
  previewRow, onSelectRow,
  onRequest, onBulkRequest, onBulkAssign, orgMembers,
  onUploadClick, onUploadForRow,
}) {
  // ── Library filter selection ────────────────────────────────
  //   "all"  → All Documents (default)
  //   "needs" → Needs Review
  //   any LIBRARY_CATEGORIES entry (e.g. "Legal") → category slice
  //   "missing"  → Missing Documents (kept separate from files)
  //   "requests" → Requests log
  const [libFilter, setLibFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("any");
  const [statusOpen, setStatusOpen] = useState(false);

  /* iter-alignment · P70 · Document Centre · Taxonomy Expansion.
   * BSA lane visibility is driven by the canonical
   * `deal.applicableBSA` SSOT (derived on the deal from
   * `property.bsa_applies`).  Default: leasehold deals surface
   * BSA unless the property explicitly flips the flag off;
   * freehold deals never surface BSA. */
  const _tenureLC = String(deal?.tenure || "").toLowerCase();
  const bsaApplies = bsaStatus(_tenureLC, optsForDeal(deal || {})) !== "no";

  /* iter-alignment · P0 · Bulk Request + Bulk Assign Reviewer
     Set of row ids currently ticked.  A row is "eligible" for
     multi-select when its `status` is either `"Missing"`
     (bulk-request) or `"Needs Review"` (bulk-assign
     reviewer) — the two bulk affordances Library operators
     have.  `bulkAction` toggles the reviewer picker so it
     never covers the table when idle. */
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const isRequestable = useCallback((row) => row && row.status === "Missing", []);
  const isAssignable  = useCallback((row) => row && row.status === "Needs Review" && !!row.uploaded_id, []);
  const isEligible = useCallback(
    (row) => isRequestable(row) || isAssignable(row),
    [isRequestable, isAssignable],
  );

  /* iter-alignment · P0 · Clear selection on view / property
     change — protects operators from a stale tick jar acting
     on the wrong deal or a filter they've already left. */
  useEffect(() => {
    setSelectedIds(new Set());
    setAssignOpen(false);
  }, [deal?.id, libFilter]);

  // ── Category / filter counts (drive the sub-nav) ────────────
  // Library + category counts = documents actually uploaded (row has an upload record /
  // Uploaded date), never required slots.  Missing + Requests keep counting outstanding items.
  const counts = useMemo(() => {
    const uploaded = rows.filter((r) => !!(r.uploaded_id || r.uploaded_at));
    const out = {
      all: uploaded.length,
      needs: uploaded.filter((r) => r.status === "Needs Review").length,
      missing: rows.filter((r) => r.status === "Missing").length,
      requests: rows.filter((r) => r.status === "Requested").length,
    };
    for (const c of LIBRARY_CATEGORIES) {
      out[c] = uploaded.filter((r) => r.category === c).length;
    }
    return out;
  }, [rows]);

  // ── Filtered rows for the main table ────────────────────────
  const visible = useMemo(() => {
    let list = rows;
    if (libFilter === "needs") list = list.filter((r) => r.status === "Needs Review");
    else if (libFilter === "missing") list = list.filter((r) => r.status === "Missing");
    else if (libFilter === "requests") list = list.filter((r) => r.status === "Requested");
    else if (LIBRARY_CATEGORIES.includes(libFilter)) list = list.filter((r) => r.category === libFilter);
    if (statusFilter !== "any") {
      list = list.filter((r) => r.status === statusFilter);
    }
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((r) =>
        String(r.label).toLowerCase().includes(q)
        || String(r.category).toLowerCase().includes(q)
        || String(r.responsible).toLowerCase().includes(q)
        || String(r.stageGate).toLowerCase().includes(q)
        || String(r.uploaded_by || "").toLowerCase().includes(q),
      );
    }
    return list;
  }, [rows, libFilter, statusFilter, query]);

  const stageLabel = String(deal?.stage || "").replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
  const targetExchange = fmtDateUK(deal?.targetExchange || deal?.target_exchange);
  const status = deal?.status || "Active";
  const statusDot = status === "At Risk" ? "#EF4444" : status === "Completed" ? "#10B981" : "#22C55E";

  const drawerOpen = !!previewRow;

  /* iter-alignment · P0 · Bulk Request From Library
     ------------------------------------------------------------
     `visibleEligible`  → the subset of `visible` that is
                          currently requestable (Missing).
     `selectedEligible` → the intersection of the current
                          selection with `visibleEligible` — this
                          is what select-all writes to, what the
                          Bulk Request bar counts, and what the
                          bulk-request handler consumes.
     Any tick that referenced a now-hidden or now-Requested row
     stays inert until the filter surfaces it again — no cleanup
     churn, no state race. */
  const visibleEligible = useMemo(
    () => visible.filter(isEligible),
    [visible, isEligible],
  );
  const selectedRows = useMemo(
    () => visibleEligible.filter((r) => selectedIds.has(r.id)),
    [visibleEligible, selectedIds],
  );
  /* Subset lookups feed the two contextual actions in the
     bulk bar — Request is only offered for the Missing
     subset, Assign for the Needs Review subset.  Either or
     both can appear depending on what the operator ticks. */
  const selectedRequestable = useMemo(
    () => selectedRows.filter(isRequestable),
    [selectedRows, isRequestable],
  );
  const selectedAssignable = useMemo(
    () => selectedRows.filter(isAssignable),
    [selectedRows, isAssignable],
  );
  const allEligibleSelected = visibleEligible.length > 0
    && visibleEligible.every((r) => selectedIds.has(r.id));
  const someEligibleSelected = visibleEligible.some((r) => selectedIds.has(r.id));

  const toggleRow = useCallback((row) => {
    if (!isEligible(row)) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(row.id)) next.delete(row.id); else next.add(row.id);
      return next;
    });
  }, [isEligible]);

  const toggleAllVisibleEligible = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allEligibleSelected) {
        for (const r of visibleEligible) next.delete(r.id);
      } else {
        for (const r of visibleEligible) next.add(r.id);
      }
      return next;
    });
  }, [visibleEligible, allEligibleSelected]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const runBulkRequest = useCallback(async () => {
    if (!onBulkRequest || selectedRequestable.length === 0) return;
    setSubmitting(true);
    try {
      const result = await onBulkRequest(selectedRequestable);
      if (result && result.ok > 0) {
        // Clear only the SUCCESSFUL request rows.  We can't
        // enumerate per-row per current API, but every
        // successful row flips out of `Missing` → filtering
        // does the work.  Failed rows are already known via
        // the toast — operator retries from row action.
        setSelectedIds((prev) => {
          const next = new Set(prev);
          for (const r of selectedRequestable) next.delete(r.id);
          return next;
        });
      }
    } finally {
      setSubmitting(false);
    }
  }, [onBulkRequest, selectedRequestable]);

  /* iter-alignment · P0 · Bulk Assign Reviewer — the picker
     resolves an org member from `orgMembers`, then hands
     off to `onBulkAssign(rows, assignee)`.  Only the SUCCESS
     ids are cleared from the tick jar; failed rows stay
     selected so the operator can retry with one tap. */
  const runBulkAssign = useCallback(async (assignee) => {
    if (!onBulkAssign || selectedAssignable.length === 0 || !assignee) return;
    setAssignSubmitting(true);
    try {
      const result = await onBulkAssign(selectedAssignable, assignee);
      if (result && (result.ok > 0 || result.fail === 0)) {
        // Remove the successful rows from the tick jar — the
        // FAILED ids stay selected for retry (per the ask).
        const failedSet = new Set(result.failedIds || []);
        setSelectedIds((prev) => {
          const next = new Set(prev);
          for (const r of selectedAssignable) {
            if (!failedSet.has(r.id)) next.delete(r.id);
          }
          return next;
        });
        if (result.fail === 0) setAssignOpen(false);
      }
    } finally {
      setAssignSubmitting(false);
    }
  }, [onBulkAssign, selectedAssignable]);

  return (
    <div data-testid="pt-library" className="w-full">
      {/* ── 1 · Title row ─────────────────────────────────────── */}
      <PageHeader
        icon={FileText}
        title="Document Centre"
        description="One trusted source for every transaction document."
        testid="pt-library-header"
        titleTestid="documents-hub-title"
        actions={(
          <>
            <button
              type="button"
              onClick={() => {
                const firstMissing = rows.find((r) => r.status === "Missing");
                if (firstMissing) onRequest(firstMissing);
                else toast("All required documents already requested or uploaded.");
              }}
              className="h-10 px-4 rounded-xl bg-white border border-slate-200 text-[13px] font-semibold text-slate-700 inline-flex items-center gap-2 hover:border-slate-300 hover:bg-slate-50 transition-all"
              data-testid="pt-library-request-btn"
              style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.03)" }}
            >
              <ClipboardList size={14} />
              Request document
            </button>
            <button
              type="button"
              onClick={onUploadClick}
              className="h-10 px-4 rounded-xl text-white text-[13px] font-semibold inline-flex items-center gap-2 transition-all hover:brightness-110"
              style={{
                background: "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)",
                boxShadow: "0 10px 24px -10px rgba(79,70,229,0.55)",
              }}
              data-testid="pt-library-upload-btn"
            >
              <Upload size={14} strokeWidth={2.4} />
              Upload document
            </button>
          </>
        )}
      />

      {/* ── 2 · Property anchor card ─────────────────────────── */}
      <PropertyAnchorCard
        deal={deal}
        property={property}
        properties={properties}
        selectedPropertyId={selectedPropertyId}
        onPropertyChange={onPropertyChange}
        propertiesLoading={propertiesLoading}
        dealByPropertyId={dealByPropertyId}
        stageLabel={stageLabel}
        targetExchange={targetExchange}
        statusLabel={status}
        statusDot={statusDot}
      />

      {/* ── 3 · Two-column body ──────────────────────────────── */}
      <div
        className={`grid gap-6 md:gap-7 mt-6 ${
          drawerOpen
            ? "grid-cols-1 xl:grid-cols-[220px_minmax(0,1fr)_380px]"
            : "grid-cols-1 xl:grid-cols-[220px_minmax(0,1fr)]"
        }`}
        data-testid="pt-library-body"
      >
        {/* LEFT — Library sub-nav */}
        <aside className="min-w-0" data-testid="pt-library-nav">
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 mb-2">
            Document Library
          </p>
          <ul className="space-y-0.5">
            <LibraryNavItem
              testid="pt-library-nav-all"
              icon={FolderOpen}
              label="All Documents"
              count={counts.all}
              active={libFilter === "all"}
              onClick={() => setLibFilter("all")}
              accent="violet"
            />
            <LibraryNavItem
              testid="pt-library-nav-needs"
              icon={Clock}
              label="Needs Review"
              count={counts.needs}
              active={libFilter === "needs"}
              onClick={() => setLibFilter("needs")}
              dot="#F59E0B"
            />
            {/* iter-alignment · P70 · BSA lane is gated on the
                canonical `deal.applicableBSA` SSOT.  Default:
                leasehold deals surface the BSA lane unless the
                property flips `bsa_applies` to false; freehold
                deals never surface BSA. */}
            {LIBRARY_CATEGORIES.filter((c) => c !== "BSA" || bsaApplies).map((c) => (
              <LibraryNavItem
                key={c}
                testid={`pt-library-nav-${slugCat(c)}`}
                icon={ICON_FOR_CATEGORY[c] || FolderOpen}
                label={c}
                count={counts[c] || 0}
                active={libFilter === c}
                onClick={() => setLibFilter(c)}
              />
            ))}
            <li className="pt-2 mt-2 border-t border-slate-100" />
            <LibraryNavItem
              testid="pt-library-nav-missing"
              icon={AlertCircle}
              label="Missing Documents"
              count={counts.missing}
              active={libFilter === "missing"}
              onClick={() => setLibFilter("missing")}
              dot="#EF4444"
              danger
            />
            <LibraryNavItem
              testid="pt-library-nav-requests"
              icon={ClipboardList}
              label="Requests"
              count={counts.requests}
              active={libFilter === "requests"}
              onClick={() => setLibFilter("requests")}
            />
          </ul>

          {/* Can't find what you need? — inline Request CTA */}
          <div
            className="mt-6 rounded-xl bg-white border border-slate-100 p-4"
            style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.03)" }}
            data-testid="pt-library-cta-request"
          >
            <span
              className="w-8 h-8 rounded-lg inline-flex items-center justify-center"
              style={{ background: "rgba(99,102,241,0.10)", color: "#4F46E5" }}
            >
              <ClipboardList size={14} />
            </span>
            <p className="text-[12.5px] font-bold text-slate-900 mt-3">
              Can&rsquo;t find what you need?
            </p>
            <p className="text-[11.5px] text-slate-500 mt-1 leading-snug">
              Request a document from the right person, with one click.
            </p>
            <button
              type="button"
              onClick={() => {
                const firstMissing = rows.find((r) => r.status === "Missing");
                if (firstMissing) onRequest(firstMissing);
                else toast("All required documents already requested or uploaded.");
              }}
              className="mt-3 text-[12px] font-semibold text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1"
              data-testid="pt-library-cta-request-btn"
            >
              Request document <ArrowRight size={12} />
            </button>
          </div>
        </aside>

        {/* MAIN — Search + Filter + Table */}
        <section className="min-w-0" data-testid="pt-library-main">
          {/* Search + Filter */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1 min-w-0">
              <FileSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search documents by name, type, stage, participant…"
                className="w-full h-11 pl-9 pr-3 rounded-xl bg-white border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 transition-colors"
                data-testid="pt-library-search"
              />
            </div>
            <div className="relative">
              <button
                type="button"
                onClick={() => setStatusOpen((v) => !v)}
                className="h-11 px-4 rounded-xl bg-white border border-slate-200 text-[13px] font-semibold text-slate-700 inline-flex items-center gap-2 hover:border-slate-300 transition-colors"
                data-testid="pt-library-filter-btn"
              >
                <FilterIcon size={13} />
                Filter
                <ChevronDown size={12} className="text-slate-400" />
              </button>
              {statusOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setStatusOpen(false)} aria-hidden />
                  <ul
                    className="absolute right-0 z-20 mt-1.5 w-[180px] rounded-xl bg-white border border-slate-200 py-1"
                    style={{ boxShadow: "0 10px 30px -10px rgba(15,23,42,0.18)" }}
                    role="listbox"
                    data-testid="pt-library-filter-menu"
                  >
                    {["any", "Needs Review", "Reviewed", "Approved", "Missing", "Requested", "Blocked"].map((s) => (
                      <li key={s}>
                        <button
                          type="button"
                          onClick={() => { setStatusFilter(s); setStatusOpen(false); }}
                          className={`w-full text-left px-3 py-1.5 text-[12px] hover:bg-slate-50 ${statusFilter === s ? "text-indigo-700 font-bold" : "text-slate-700"}`}
                          data-testid={`pt-library-filter-opt-${s.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          {s === "any" ? "Any status" : s}
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>

          {/* Table */}
          <div
            className="mt-4 rounded-2xl bg-white border border-slate-100 overflow-hidden"
            style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.03)" }}
            data-testid="pt-library-table"
          >
            {/* iter-alignment · P0 · Bulk Request + Bulk Assign
                Reviewer bar — rendered ONLY when ≥1 eligible
                row is ticked.  Actions surface contextually:
                Request only when a Missing row is in the
                selection; Assign only when a Needs Review
                row is in the selection.  The reviewer picker
                sits behind an anchored menu so idle state
                stays calm. */}
            {selectedRows.length > 0 && (
              <div
                className="px-4 py-3 border-b border-indigo-100 bg-indigo-50/60 flex items-center justify-between gap-3"
                data-testid="pt-library-bulk-bar"
                role="region"
                aria-label="Bulk actions"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    className="text-[12px] font-bold text-indigo-800 tabular-nums"
                    data-testid="pt-library-bulk-count"
                  >
                    {selectedRows.length} selected
                  </span>
                  <button
                    type="button"
                    onClick={clearSelection}
                    className="text-[11.5px] font-semibold text-indigo-700/80 hover:text-indigo-900 transition-colors"
                    data-testid="pt-library-bulk-clear"
                  >
                    Clear
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  {selectedRequestable.length > 0 && (
                    <button
                      type="button"
                      onClick={runBulkRequest}
                      disabled={submitting}
                      className="h-8 px-3 rounded-lg bg-white border border-indigo-200 text-indigo-700 text-[12px] font-bold inline-flex items-center gap-1.5 transition-all hover:bg-indigo-50 disabled:opacity-70"
                      data-testid="pt-library-bulk-request-btn"
                    >
                      {submitting ? <Loader2 size={12} className="animate-spin" /> : <ClipboardList size={12} />}
                      Request selected ({selectedRequestable.length})
                    </button>
                  )}
                  {selectedAssignable.length > 0 && (
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setAssignOpen((v) => !v)}
                        disabled={assignSubmitting}
                        className="h-8 px-3 rounded-lg text-white text-[12px] font-bold inline-flex items-center gap-1.5 transition-all hover:brightness-110 disabled:opacity-70"
                        style={{
                          background: "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)",
                          boxShadow: "0 6px 16px -8px rgba(79,70,229,0.55)",
                        }}
                        data-testid="pt-library-bulk-assign-btn"
                        aria-expanded={assignOpen}
                        aria-haspopup="menu"
                      >
                        {assignSubmitting ? <Loader2 size={12} className="animate-spin" /> : <User size={12} />}
                        Assign reviewer ({selectedAssignable.length})
                        <ChevronDown size={11} className="opacity-80" />
                      </button>
                      {assignOpen && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setAssignOpen(false)} aria-hidden />
                          <ul
                            className="absolute right-0 z-20 mt-1.5 w-[240px] max-h-[280px] overflow-y-auto rounded-xl bg-white border border-slate-200 py-1"
                            style={{ boxShadow: "0 10px 30px -10px rgba(15,23,42,0.18)" }}
                            role="menu"
                            data-testid="pt-library-assign-menu"
                          >
                            <li className="px-3 py-1.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-500">
                              Assign to
                            </li>
                            {(orgMembers || []).length === 0 && (
                              <li className="px-3 py-2 text-[12px] text-slate-500" data-testid="pt-library-assign-empty">
                                No members in this organisation yet.
                              </li>
                            )}
                            {(orgMembers || []).map((m) => (
                              <li key={m.user_id} role="none">
                                <button
                                  type="button"
                                  role="menuitem"
                                  onClick={() => runBulkAssign(m)}
                                  disabled={assignSubmitting}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-[12.5px] hover:bg-slate-50 transition-colors disabled:opacity-60"
                                  data-testid={`pt-library-assign-option-${m.user_id}`}
                                >
                                  <span
                                    className="w-6 h-6 rounded-full inline-flex items-center justify-center text-[10px] font-bold text-slate-700 shrink-0"
                                    style={{ background: "#F1F5F9" }}
                                    aria-hidden
                                  >
                                    {initialsOf(m.full_name || m.email || "")}
                                  </span>
                                  <span className="flex-1 min-w-0">
                                    <span className="block truncate text-slate-900 font-semibold">
                                      {m.full_name || m.email || "Member"}
                                    </span>
                                    {m.role && (
                                      <span className="block text-[10.5px] text-slate-500 truncate">
                                        {m.role.replace(/_/g, " ")}
                                      </span>
                                    )}
                                  </span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Header row */}
            <div className="grid grid-cols-[28px_minmax(220px,2.4fr)_minmax(120px,1fr)_minmax(120px,1fr)_minmax(110px,1fr)_minmax(140px,1fr)_minmax(110px,0.9fr)_36px] gap-3 px-4 py-3 border-b border-slate-100 text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-500 bg-slate-50/40 items-center">
              {/* Select-all checkbox — disabled when there is
                  no eligible visible row so operators are never
                  offered a no-op tick. */}
              <input
                type="checkbox"
                aria-label="Select all missing documents in view"
                disabled={visibleEligible.length === 0}
                checked={allEligibleSelected}
                ref={(el) => { if (el) el.indeterminate = !allEligibleSelected && someEligibleSelected; }}
                onChange={toggleAllVisibleEligible}
                className="w-3.5 h-3.5 accent-indigo-600 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="pt-library-select-all"
              />
              <span>Document</span>
              <span>Category</span>
              <span>Stage</span>
              <span>Uploaded</span>
              <span>Added by</span>
              <span>Status</span>
              <span aria-hidden />
            </div>

            {/* Body */}
            {loading ? (
              <div className="px-4 py-14 flex items-center justify-center text-slate-400 text-[12px]">
                <Loader2 size={14} className="mr-2 animate-spin" /> Loading library…
              </div>
            ) : visible.length === 0 ? (
              <div className="px-4 py-14 text-center text-[12.5px] text-slate-500" data-testid="pt-library-empty">
                No documents match this view. Try a different filter, or upload / request a document.
              </div>
            ) : (
              <ul>
                {visible.map((row) => (
                  <LibraryRow
                    key={row.id}
                    row={row}
                    onSelect={() => onSelectRow(row)}
                    active={previewRow?.id === row.id}
                    onUpload={() => onUploadForRow(row)}
                    onRequest={() => onRequest(row)}
                    selectable={isEligible(row)}
                    selected={selectedIds.has(row.id)}
                    onToggle={() => toggleRow(row)}
                  />
                ))}
              </ul>
            )}

            {/* Footer count */}
            <div className="px-4 py-3 border-t border-slate-100 text-[11.5px] text-slate-500">
              {/* Slots = every derived row; uploaded ⊆ slots, so "without uploads" never double-counts
                  and is not the Missing (required) count. */}
              {visible.length !== rows.length && <>Showing {visible.length} of </>}
              <span data-testid="pt-library-slot-count">{rows.length} document slot{rows.length === 1 ? "" : "s"}</span>
              {" · "}<span data-testid="pt-library-uploaded-count">{counts.all} uploaded</span>
              {" · "}<span data-testid="pt-library-without-upload-count">{rows.length - counts.all} without uploads</span>
              {libFilter !== "all" && ` in ${libFilter === "needs" ? "Needs Review" : libFilter === "missing" ? "Missing Documents" : libFilter === "requests" ? "Requests" : libFilter}`}
            </div>
          </div>
        </section>

        {/* The preview drawer is rendered by DocumentsPage as a
            fixed-overlay right sheet — layout above simply
            reserves a 380px column when it's open so the table
            reclaims full width when closed. */}
      </div>
    </div>
  );
}

/* ─── Property anchor card ────────────────────────────────── */
function PropertyAnchorCard({
  deal, property, properties, selectedPropertyId,
  onPropertyChange, propertiesLoading, dealByPropertyId,
  stageLabel, targetExchange, statusLabel, statusDot,
}) {
  const [open, setOpen] = useState(false);
  const address = property?.address || deal?.name || "Select a property";
  const postcode = property?.postcode || "";
  const rows = Array.isArray(properties) ? properties : [];
  const isEmpty = !propertiesLoading && rows.length === 0;
  return (
    <div
      className="mt-6 rounded-2xl bg-white border border-slate-100 p-4 md:p-5 flex items-center gap-5 flex-wrap"
      style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.03)" }}
      data-testid="pt-library-property-card"
    >
      {/* Thumbnail */}
      <span
        className="w-14 h-14 rounded-xl shrink-0 inline-flex items-center justify-center overflow-hidden"
        style={{ background: "linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)", color: "#4F46E5" }}
        aria-hidden
      >
        <Home size={22} strokeWidth={2} />
      </span>

      {/* Address / property selector — iter-alignment · P2 ·
          Document Centre Property Selector (2026-02-28) — values
          are canonical property ids; the list is the SAME
          `properties` slice the Properties page reads. */}
      <div className="min-w-0 flex-1">
        <div className="relative">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-2 text-left"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label="Change property"
            data-testid="pt-library-property-selector"
            data-selected-property-id={selectedPropertyId || ""}
          >
            <span className="text-[16px] md:text-[18px] font-bold tracking-[-0.01em] text-slate-900 truncate max-w-[420px]">
              {address}
            </span>
            <ChevronDown size={14} className="text-slate-400 shrink-0" />
          </button>
          {open && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden />
              <ul
                className="absolute z-20 mt-2 min-w-[320px] max-h-[300px] overflow-y-auto rounded-xl bg-white border border-slate-200 py-1 cf-scroll"
                style={{ boxShadow: "0 10px 30px -10px rgba(15,23,42,0.18)" }}
                role="listbox"
                aria-label="Available properties"
                data-testid="pt-library-property-listbox"
              >
                {propertiesLoading && (
                  <li
                    className="px-3 py-2 text-[12.5px] text-slate-500"
                    data-testid="pt-library-property-loading"
                  >
                    Loading properties…
                  </li>
                )}
                {isEmpty && (
                  <li
                    className="px-3 py-2 text-[12.5px] text-slate-500"
                    data-testid="pt-library-property-empty"
                  >
                    No properties available. Add one from the Properties page.
                  </li>
                )}
                {rows.map((p) => {
                  const isCur = p.id === selectedPropertyId;
                  const linkedDeal = dealByPropertyId ? dealByPropertyId.get(p.id) : null;
                  return (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => { onPropertyChange && onPropertyChange(p.id); setOpen(false); }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-left text-[12.5px] hover:bg-slate-50 transition-colors ${isCur ? "text-indigo-700 font-bold" : "text-slate-700"}`}
                        role="option"
                        aria-selected={isCur}
                        data-testid={`pt-library-property-option-${p.id}`}
                        data-property-id={p.id}
                      >
                        <span
                          className="w-1.5 h-1.5 rounded-full shrink-0"
                          style={{ background: isCur ? "#4F46E5" : "#CBD5E1" }}
                          aria-hidden
                        />
                        <span className="truncate flex-1">
                          {p.address || "Untitled property"}
                        </span>
                        {!linkedDeal && (
                          <span
                            className="text-[10.5px] font-medium text-slate-400 shrink-0"
                            data-testid={`pt-library-property-option-no-deal-${p.id}`}
                            title="No active transaction linked to this property yet"
                          >
                            No transaction
                          </span>
                        )}
                        {isCur && <Check size={12} />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
        {postcode && (
          <p className="text-[12px] text-slate-500 mt-0.5 truncate max-w-[420px]">{postcode}</p>
        )}
      </div>

      {/* Meta chips — Status · Current Stage · Target Exchange */}
      <div className="flex items-center gap-6 flex-wrap">
        <AnchorChip
          label="Status"
          value={
            <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-900">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusDot }} aria-hidden />
              {statusLabel}
            </span>
          }
          testid="pt-library-anchor-status"
        />
        <AnchorChip label="Stage" value={stageLabel || "—"} testid="pt-library-anchor-stage" />
        <AnchorChip label="Target exchange" value={targetExchange || "—"} testid="pt-library-anchor-target-exchange" />
      </div>
    </div>
  );
}

function AnchorChip({ label, value, testid }) {
  return (
    <div className="min-w-0" data-testid={testid}>
      <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-500">{label}</p>
      <p className="text-[13px] font-semibold text-slate-900 mt-0.5">{value}</p>
    </div>
  );
}

/* ─── Library sub-nav row ─────────────────────────────────── */
const ICON_FOR_CATEGORY = {
  "Property": Home,
  "Sales": FileText,
  "Legal": FileSignature,
  "Management": FolderOpen,
  "BSA": ShieldAlert,
  "Mortgage & Financial": Landmark,
  "Compliance": ShieldCheck,
  "Repairs & Maintenance": Wrench,
  "Utilities": Receipt,
  "Exchange": FileBadge,
  "Completion": Check,
};

const slugCat = (c) => c.toLowerCase().replace(/\s*&\s*/g, "-").replace(/\//g, "-").replace(/\s+/g, "-");

function LibraryNavItem({ testid, icon: Icon, label, count, active, onClick, accent, dot, danger }) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12.5px] font-semibold transition-colors ${
          active
            ? "bg-indigo-50/70 text-indigo-800 border border-indigo-100"
            : "text-slate-700 hover:bg-slate-50 border border-transparent"
        }`}
        data-testid={testid}
        aria-current={active ? "page" : undefined}
      >
        <span
          className="w-5 h-5 rounded-md inline-flex items-center justify-center shrink-0"
          style={{
            background: active ? "rgba(79,70,229,0.14)" : "transparent",
            color: active ? "#4338CA" : (danger ? "#B91C1C" : "#475569"),
          }}
        >
          {dot ? (
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: dot }} aria-hidden />
          ) : (
            <Icon size={13} strokeWidth={2.1} />
          )}
        </span>
        <span className="flex-1 text-left truncate">{label}</span>
        <span className="text-[11px] font-bold text-slate-500 tabular-nums">{count}</span>
      </button>
    </li>
  );
}

/* ─── Library table row ───────────────────────────────────── */
function LibraryRow({ row, onSelect, active, onUpload, onRequest, selectable, selected, onToggle }) {
  const tone = STATUS_TONE[row.status] || STATUS_TONE.Missing;
  const stageLabel = String(row.stageGate || "").replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
  const uploadedText = fmtDateUK(row.uploaded_at);
  const initials = row.uploaded_by ? initialsOf(row.uploaded_by) : null;
  const hasUpload = !!row.uploaded_id;

  const onRowKey = (e) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    if (e.target?.closest?.('input,button,label,[data-no-row-click]')) return;
    e.preventDefault();
    onSelect();
  };

  return (
    <li>
      <div
        className={`w-full grid grid-cols-[28px_minmax(220px,2.4fr)_minmax(120px,1fr)_minmax(120px,1fr)_minmax(110px,1fr)_minmax(140px,1fr)_minmax(110px,0.9fr)_36px] gap-3 px-4 py-3.5 border-t border-slate-50 items-center transition-colors cursor-pointer ${
          active ? "bg-indigo-50/40" : "hover:bg-slate-50/60"
        }`}
        data-testid={`pt-library-row-${row.id}`}
        onClick={(e) => {
          // Row-select opens the drawer.  Skip when the click
          // came from the checkbox, its label, or any inner
          // action so per-cell affordances stay intact.
          if (e.target?.closest?.('input,label,button,[data-no-row-click]')) return;
          onSelect();
        }}
        onKeyDown={onRowKey}
        role="button"
        tabIndex={0}
        aria-label={`Open ${row.label} details`}
      >
        {/* Checkbox cell — real HTML input for accessibility;
            rendered inert when the row isn't requestable so the
            column always aligns. */}
        <label
          className={`inline-flex items-center justify-center ${selectable ? "cursor-pointer" : "opacity-30 cursor-not-allowed"}`}
          data-no-row-click
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            className="w-3.5 h-3.5 accent-indigo-600"
            aria-label={selectable ? `Select ${row.label} for bulk request` : `${row.label} is not requestable`}
            disabled={!selectable}
            checked={!!selected}
            onChange={onToggle}
            data-testid={`pt-library-row-checkbox-${row.id}`}
          />
        </label>

        {/* Document */}
        <div className="flex items-center gap-3 min-w-0">
          <span
            className="w-9 h-9 rounded-lg shrink-0 inline-flex items-center justify-center"
            style={{ background: hasUpload ? "rgba(99,102,241,0.10)" : "rgba(148,163,184,0.14)", color: hasUpload ? "#4F46E5" : "#64748B" }}
            aria-hidden
          >
            <FileText size={15} strokeWidth={2.1} />
          </span>
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-slate-900 truncate" data-testid={`pt-library-row-name-${row.id}`}>
              {row.label}
            </p>
            {row.size && (
              <p className="text-[11px] text-slate-500 mt-0.5 tabular-nums">
                {(row.size / 1024).toFixed(0)} KB
              </p>
            )}
          </div>
        </div>

        {/* Category chip */}
        <span
          className="inline-flex items-center h-6 px-2 rounded-full text-[10.5px] font-bold uppercase tracking-[0.06em] w-fit"
          style={{ background: "rgba(99,102,241,0.10)", color: "#4338CA" }}
          data-testid={`pt-library-row-category-${row.id}`}
        >
          {row.category}
        </span>

        {/* Stage */}
        <span className="text-[12.5px] text-slate-700 truncate">{stageLabel || "—"}</span>

        {/* Uploaded */}
        <span className="text-[12.5px] text-slate-700 tabular-nums">{uploadedText || "—"}</span>

        {/* Added by */}
        <span className="text-[12.5px] text-slate-700 inline-flex items-center gap-2 truncate min-w-0">
          <span className="inline-flex items-center gap-2 min-w-0">
            {initials ? (
              <span
                className="w-6 h-6 rounded-full inline-flex items-center justify-center text-[10px] font-bold text-slate-700"
                style={{ background: "#F1F5F9" }}
                aria-hidden
              >
                {initials}
              </span>
            ) : null}
            <span className="truncate">{row.uploaded_by || "—"}</span>
          </span>
          {row.assignee_name ? (
            <span
              className="inline-flex items-center gap-1 h-5 px-1.5 rounded-full text-[10px] font-bold ml-1 shrink-0"
              style={{ background: "rgba(99,102,241,0.10)", color: "#4338CA" }}
              title={`Reviewer: ${row.assignee_name}`}
              data-testid={`pt-library-row-assignee-${row.id}`}
            >
              <User size={9} strokeWidth={2.5} />
              <span className="truncate max-w-[80px]">{row.assignee_name}</span>
            </span>
          ) : null}
        </span>

        {/* Status */}
        <span
          className="inline-flex items-center h-6 px-2.5 rounded-full text-[11px] font-bold w-fit"
          style={{ background: tone.bg, color: tone.fg, boxShadow: `inset 0 0 0 1px ${tone.ring}` }}
          data-testid={`pt-library-row-status-${row.id}`}
        >
          {row.status}
        </span>

        {/* Row-end kebab (visual only — actions live in drawer) */}
        <span className="text-slate-300" aria-hidden>
          <MoreHorizontal size={14} />
        </span>
      </div>
    </li>
  );
}


/* iter-alignment · Preview-panel dropzone (2026-09)
 * ---------------------------------------------------------------
 * Turns the "No file uploaded yet" empty state into a full drop
 * zone that reuses the existing `/api/deals/{id}/documents`
 * upload path.  Automatically inherits the drawer's transaction,
 * document type, category and responsible party.  After file
 * selection the operator confirms with "Upload document".
 * Backend-wide accept list — preview support stays separate.
 */
const _DROPZONE_ACCEPT = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.oasis.opendocument.spreadsheet",
  "image/png", "image/jpeg", "image/webp", "image/heic", "image/heif",
  "text/csv", "text/plain",
  "application/zip", "application/x-zip-compressed",
].join(",");

function PreviewDropzone({ row, onUpload, uploading }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [staged, setStaged] = useState(null);

  const handleFiles = useCallback((fileList) => {
    const f = fileList && fileList[0];
    if (!f) return;
    setStaged({ file: f, name: f.name, size: f.size });
  }, []);
  const onDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    handleFiles(e.dataTransfer?.files);
  }, [handleFiles]);
  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragging) setDragging(true);
  }, [dragging]);
  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  }, []);
  const openPicker = useCallback(() => inputRef.current?.click(), []);
  const confirmUpload = useCallback(() => {
    if (!staged?.file || uploading) return;
    onUpload?.(row, staged.file);
    setStaged(null);
    if (inputRef.current) inputRef.current.value = "";
  }, [staged, row, onUpload, uploading]);
  const clearStaged = useCallback(() => {
    setStaged(null);
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  return (
    <div
      className={`rounded-xl bg-white p-6 min-h-[220px] flex flex-col items-center justify-center text-center transition-colors ${
        dragging
          ? "border-2 border-dashed border-indigo-400 bg-indigo-50/60"
          : "border-2 border-dashed border-slate-200"
      }`}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragEnter={onDragOver}
      onDragLeave={onDragLeave}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openPicker();
        }
      }}
      onClick={() => { if (!staged) openPicker(); }}
      aria-label={`Drop the ${row.label} here or choose a file`}
      data-testid="documents-hub-preview-dropzone"
      data-dragging={dragging ? "1" : "0"}
    >
      <input
        ref={inputRef}
        type="file"
        accept={_DROPZONE_ACCEPT}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid="documents-hub-preview-dropzone-input"
      />
      {!staged ? (
        <>
          <span
            className="w-14 h-14 rounded-xl inline-flex items-center justify-center mb-3"
            style={{ background: "rgba(99,102,241,0.08)", color: "#6366F1" }}
          >
            <UploadCloud size={22} strokeWidth={2.1} />
          </span>
          <p className="text-[13.5px] font-semibold text-slate-800 leading-tight">
            {dragging ? `Release to upload the ${row.label.toLowerCase()}` : `Drop the ${row.label} here`}
          </p>
          <p className="text-[11.5px] text-slate-500 mt-1.5 leading-snug">
            PDF, Office documents, images, CSV, text or ZIP · Maximum 50&nbsp;MB
          </p>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openPicker(); }}
            className="mt-4 h-9 px-4 rounded-lg text-[12.5px] font-semibold text-white transition-shadow"
            style={{ background: "linear-gradient(135deg,#8B5CF6 0%,#6366F1 100%)" }}
            data-testid="documents-hub-preview-dropzone-browse"
          >
            <span className="hidden sm:inline">Browse files</span>
            <span className="sm:hidden">Choose file</span>
          </button>
          <p className="mt-3 text-[10.5px] text-slate-400 leading-snug">
            Filed against <span className="font-semibold text-slate-500">{row.label}</span>
            {row.responsible ? <> · {row.responsible}</> : null}
          </p>
        </>
      ) : (
        <div className="w-full max-w-[300px] mx-auto" data-testid="documents-hub-preview-dropzone-staged">
          <span
            className="w-12 h-12 rounded-xl inline-flex items-center justify-center mb-3 mx-auto"
            style={{ background: "rgba(16,185,129,0.10)", color: "#059669" }}
          >
            <FileText size={20} strokeWidth={2.1} />
          </span>
          <p className="text-[12.5px] font-semibold text-slate-800 truncate" title={staged.name} data-testid="documents-hub-preview-dropzone-filename">
            {staged.name}
          </p>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {staged.size < 1024
              ? "<1 KB"
              : `${(staged.size / 1024).toFixed(0)} KB`}
            &nbsp;· ready to upload against {row.label}
          </p>
          <div className="mt-4 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); clearStaged(); }}
              disabled={uploading}
              className="h-9 px-3 rounded-lg text-[12px] font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-40"
              data-testid="documents-hub-preview-dropzone-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); confirmUpload(); }}
              disabled={uploading}
              className="h-9 px-4 rounded-lg text-[12.5px] font-semibold text-white inline-flex items-center gap-1.5 transition-shadow disabled:opacity-70"
              style={{ background: "linear-gradient(135deg,#8B5CF6 0%,#6366F1 100%)" }}
              data-testid="documents-hub-preview-dropzone-confirm"
            >
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <UploadCloud size={12} />}
              {uploading ? "Uploading…" : "Upload document"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


/* ─── Header ───────────────────────────────────────────────────── */
function DocumentPreviewDrawer({
  row, open, onClose,
  onView, onDownload, onReplace, onUpload,
  onRequestDelete, onCancelDelete, onConfirmDelete,
  confirmingDelete, deleting, replacing, uploading,
}) {
  const [tab, setTab] = useState("preview");
  if (!open || !row) return null;
  const Icon = row.icon || FileText;
  const tone = STATUS_TONE[row.status] || STATUS_TONE.Missing;
  const hasUpload = !!row.uploaded_id;
  if (typeof document === "undefined") return null;
  /* iter-alignment · Doc Attribution (2026-09) — portal the
   * drawer to <body> so `fixed inset-0` escapes the App shell's
   * `.cf-page-enter` transformed ancestor.  Without this, the
   * drawer's containing block is the (multi-thousand-pixel)
   * page wrapper and the footer actions render off-screen on
   * desktop / the whole drawer opens off-viewport on mobile.
   * Same fix pattern as `components/dealroom/DocumentReviewModal`. */
  const drawer = (
    <div
      /* iter-alignment · P0 · Drawer top-cropping fix
         ------------------------------------------------
         The global TopCommandRail (`--cf-topbar-h`, 56 px)
         renders ONLY on `xl+` viewports.  Below xl the rail
         is `display:none` so the drawer sits at top:0.  On
         xl+ we offset the drawer by `--cf-topbar-h` (matches
         the same pattern the App.js content wrapper uses:
         `xl:pt-[var(--cf-topbar-h,0px)]`), guaranteeing the
         drawer's OWN header (title / status / close) opens
         directly below the rail and stays fully visible on
         every viewport. */
      className="fixed left-0 right-0 bottom-0 top-0 xl:top-[var(--cf-topbar-h,0px)] z-50 flex"
      data-testid="documents-hub-preview-drawer"
      role="dialog"
      aria-modal="true"
      aria-label={`${row.label} details`}
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close preview"
        onClick={onClose}
        className="flex-1 bg-slate-900/30 backdrop-blur-[2px] transition-opacity"
        data-testid="documents-hub-preview-backdrop"
      />
      {/* Sheet */}
      <aside
        className="w-full sm:w-[420px] h-full bg-white border-l border-slate-100 shadow-[0_20px_50px_-10px_rgba(15,23,42,0.18)] flex flex-col"
        data-testid="documents-hub-preview-sheet"
      >
        {/* Header */}
        <header className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <span
              className="w-10 h-10 rounded-lg inline-flex items-center justify-center shrink-0"
              style={{ background: "#F1F5F9", color: "#475569" }}
            >
              <Icon size={16} strokeWidth={2.2} />
            </span>
            <div className="min-w-0">
              <h3 className="text-[15px] font-bold text-slate-900 leading-tight truncate" data-testid="documents-hub-preview-title">
                {row.label}
              </h3>
              <p className="text-[11.5px] text-slate-500 mt-0.5">{row.responsible}</p>
              <div className="mt-2"><StatusPill status={row.status} tone={tone} /></div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-md inline-flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100"
            data-testid="documents-hub-preview-close"
          >
            <X size={14} />
          </button>
        </header>

        {/* iter-P0 · Library preview tabs */}
        <div className="px-5 pt-3 border-b border-slate-100" data-testid="documents-hub-preview-tabs">
          <div role="tablist" className="flex items-center gap-4 -mb-px">
            {[
              { id: "preview", label: "Preview" },
              { id: "details", label: "Details" },
              { id: "activity", label: "Activity" },
              { id: "linked", label: "Linked" },
            ].map(({ id, label }) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={tab === id}
                onClick={() => setTab(id)}
                className={`h-8 text-[12px] font-semibold transition-colors border-b-2 px-0.5 ${
                  tab === id
                    ? "text-indigo-700 border-indigo-500"
                    : "text-slate-500 border-transparent hover:text-slate-700"
                }`}
                data-testid={`documents-hub-preview-tab-${id}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        {tab === "preview" && (
          <div className="flex-1 px-5 py-5 overflow-y-auto bg-slate-50/50" data-testid="documents-hub-preview-panel-preview">
            {hasUpload ? (
              <div
                className="rounded-xl bg-white border border-slate-200 p-8 flex flex-col items-center justify-center text-center min-h-[200px]"
                data-testid="documents-hub-preview-canvas"
              >
                <span
                  className="w-14 h-14 rounded-xl inline-flex items-center justify-center mb-3"
                  style={{ background: "rgba(99,102,241,0.08)", color: "#6366F1" }}
                >
                  <FileText size={22} strokeWidth={2.1} />
                </span>
                <p className="text-[13px] font-semibold text-slate-700">{row.label}</p>
                <p className="text-[11.5px] text-slate-500 mt-1">Click View to open the document in a new tab.</p>
              </div>
            ) : (
              <PreviewDropzone row={row} onUpload={onUpload} uploading={uploading} />
            )}
          </div>
        )}

        {tab === "details" && (
          <div className="flex-1 px-5 py-4 space-y-3 overflow-y-auto" data-testid="documents-hub-preview-panel-details">
            <DrawerMetaRow label="Category" value={row.category || "—"} />
            <DrawerMetaRow label="Stage" value={String(row.stageGate || "").replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()) || "—"} />
            <DrawerMetaRow label="Uploaded" value={fmtDateUK(row.uploaded_at) || "—"} />
            <DrawerMetaRow label="Added by" value={row.uploaded_by || "—"} />
            <DrawerMetaRow label="Reviewer" value={row.assignee_name || "Unassigned"} />
            <DrawerMetaRow label="Responsible" value={row.responsible || "—"} />
            {row.size ? <DrawerMetaRow label="File size" value={`${(row.size / 1024).toFixed(0)} KB`} /> : null}
            <DrawerMetaRow label="Review status" value={row.status} />
          </div>
        )}

        {tab === "activity" && (
          <div className="flex-1 px-5 py-4 overflow-y-auto text-[12px] text-slate-600 space-y-2" data-testid="documents-hub-preview-panel-activity">
            {row.uploaded_at ? (
              <p>Uploaded {fmtDateUK(row.uploaded_at)}{row.uploaded_by ? ` by ${row.uploaded_by}` : ""}.</p>
            ) : row._request ? (
              <p>Requested{row._request.created_at ? ` on ${fmtDateUK(row._request.created_at)}` : ""}.</p>
            ) : (
              <p>No activity yet for this document.</p>
            )}
          </div>
        )}

        {tab === "linked" && (
          <div className="flex-1 px-5 py-4 overflow-y-auto text-[12px] text-slate-600 space-y-2" data-testid="documents-hub-preview-panel-linked">
            <DrawerMetaRow label="Deal Room" value={row._upload?.deal_name || "This transaction"} />
            <DrawerMetaRow label="Property" value={row._upload?.property_address || "—"} />
            <p className="text-[11.5px] text-slate-500 mt-2">
              This document is stored once and appears everywhere it is linked — the Deal Room, this Library and any participant view with permissions.
            </p>
          </div>
        )}

        {/* Actions */}
        <footer className="px-5 py-4 border-t border-slate-100 flex items-center gap-2 flex-wrap" data-testid="documents-hub-preview-actions">
          <DrawerActionBtn
            icon={Eye}
            label="View"
            disabled={!hasUpload}
            onClick={onView}
            testid="documents-hub-preview-view"
          />
          <DrawerActionBtn
            icon={Download}
            label="Download"
            disabled={!hasUpload}
            onClick={onDownload}
            testid="documents-hub-preview-download"
          />
          <DrawerActionBtn
            icon={RefreshCw}
            label={replacing ? "Replacing…" : "Replace"}
            disabled={!hasUpload || replacing}
            onClick={onReplace}
            testid="documents-hub-preview-replace"
          />
          <DrawerActionBtn
            icon={Trash2}
            label="Delete"
            danger
            disabled={!hasUpload || deleting}
            onClick={onRequestDelete}
            testid="documents-hub-preview-delete"
          />
        </footer>

        {/* Confirm delete strip */}
        {confirmingDelete && (
          <div
            className="px-5 py-4 border-t border-rose-100 bg-rose-50/60"
            data-testid="documents-hub-preview-delete-confirm"
            role="alertdialog"
            aria-label="Confirm document deletion"
          >
            <p className="text-[12.5px] font-semibold text-rose-700 leading-snug">
              Delete this document?
            </p>
            <p className="text-[11.5px] text-rose-700/80 mt-0.5">
              This removes the file from the deal. The action cannot be undone.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={onCancelDelete}
                disabled={deleting}
                className="h-8 px-3 rounded-md text-[12px] font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50"
                data-testid="documents-hub-preview-delete-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onConfirmDelete}
                disabled={deleting}
                className="h-8 px-3 rounded-md text-[12px] font-bold text-white inline-flex items-center gap-1.5 transition-colors"
                style={{ background: "#DC2626", boxShadow: "0 4px 14px -6px rgba(220,38,38,0.55)" }}
                data-testid="documents-hub-preview-delete-confirm-btn"
              >
                {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                {deleting ? "Deleting…" : "Yes, delete"}
              </button>
            </div>
          </div>
        )}
      </aside>
    </div>
  );

  return createPortal(drawer, document.body);
}

function DrawerMetaRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-[12px]">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-800 font-semibold truncate">{value}</span>
    </div>
  );
}

function DrawerActionBtn({ icon: Icon, label, onClick, disabled, danger, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className="h-9 px-3 rounded-md text-[12px] font-bold inline-flex items-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      style={
        danger
          ? { color: "#B91C1C", background: "#FEF2F2", border: "1px solid rgba(239,68,68,0.28)" }
          : { color: "#1E293B", background: "#FFFFFF", border: "1px solid #E2E8F0" }
      }
    >
      <Icon size={12} />
      {label}
    </button>
  );
}


function StatusPill({ status, tone }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 h-[22px] px-2 rounded-md text-[10.5px] font-bold whitespace-nowrap"
      style={{ background: tone.bg, color: tone.fg, border: `1px solid ${tone.ring}` }}
      data-testid={`docs-status-${status.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: tone.dot }} aria-hidden />
      {status}
    </span>
  );
}

